/* eslint-disable jsx-a11y/img-redundant-alt */
import {
    doc,
    getDoc,
    getDocs,
    query,
    Timestamp,
    where,
} from "firebase/firestore";

import React, {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import {
    Link,
    useNavigate,
    useParams,
    useSearchParams,
} from "react-router-dom";
import {
    addFighterToEvent,
    applyWeightToRegistrations,
    createEvent,
    eventFightersCollection,
    eventsCollection,
    fighterRequestsCollection,
    fetchActiveTokenCountsForFighters,
    fightersCollection,
    getFighters,
    matchEventFighter,
    pullOutFighter,
    refundEventFighter,
    rejoinEventFighterWithToken,
    removeFighterFromEvent,
    setEventBoutOrder,
    setEventTestMode,
    setEventPartnerHoldScores,
    swapBoutCorners,
    subscribeEventJudges,
    subscribeEventScorecards,
    unMatchEventFighter,
    updateEvent,
    updateRecords,
    uploadEventCoverImage,
} from "../utils/firebase";
import toast from "react-hot-toast";
import {
    ArrowLeftRight,
    CircleCheck,
    CircleHelp,
    CircleX,
    ChevronDown,
    ChevronUp,
    Clipboard,
    FileSpreadsheet,
    Gavel,
    Crown,
    FlaskConical,
    GripVertical,
    ImageOff,
    LinkIcon,
    Lock,
    Mars,
    MonitorPlay,
    Plus,
    Skull,
    TriangleAlert,
    UploadCloudIcon,
    Venus,
    X,
} from "lucide-react";
import {
    extractTimestamp,
    formatCurrency,
    formatFirestoreTimestamp,
    formatPhoneNumberForWhatsApp,
    getEventFighterWeightClass,
    getLiveStreamUrls,
    getRecommendedOpponents,
    getUniqueMatchedFighters,
    getYouTubeVideoId,
    isMac,
} from "../utils/common";
import WeightClassPicker from "../components/weight-class-picker";
import Markdown from "react-markdown";
import SelectInput from "../components/select-input";
import {
    fighterEnties,
    fighterEntryDisplayMapping,
    genders,
    monthMap,
    months,
    weightClasses,
    weightClassesName,
} from "../constants";
import { twMerge } from "tailwind-merge";
import FighterMatchingCard from "../components/fighter-matching-card";
import EventFighterListItem from "../components/event-fighter-list-item";
import EventFighterFilterBar from "../components/event-fighter-filter-bar";
import {
    applyBoutOrder,
    defaultSortForTab,
    emptyFilters,
    filtersToParams,
    moveBout,
    hasActiveFilters,
    isValidSortForTab,
    matchesFilters,
    matchesFiltersPair,
    parseFiltersFromParams,
    sortEventFighters,
    sortPairEntries,
} from "../utils/event-fighter-filters";
import { EVENT_RESULTS_PATH, LIVE_STREAM_PATH, MANAGE_EVENT_PATH, MANAGE_FIGHTER_REQUEST_PATH, REGISTER_EVENT_PATH } from "../App";
import Papa from "papaparse";
import VideoPlayer from "../components/video-player";
import { FaWhatsapp } from "react-icons/fa";
import EventReadinessPanel from "../components/event-readiness-panel";
import EventJudgesPanel from "../components/event-judges-panel";
import BoutJudgeStrip from "../components/bout-judge-strip";
import {
    boutVerdict,
    hasResult,
    isMmaPair,
    judgeSuggestion,
    resolveCorners,
} from "../utils/judge-scoring";
import {
    buildFightCard,
    fightCardFileName,
    writeFightCardWorkbook,
} from "../utils/fight-card-export";
import {
    endEventBlockedText,
    endEventReadiness,
    isPulledOutPair,
} from "../utils/end-event-readiness";

// The Event Fighters tab values, in strip order. Also the accepted values of
// the ?tab= search param. "judges" only exists once the event is saved.
const EVENT_FIGHTER_TABS = ["all", "matched", "pulledOut", "refunded", "judges"];

// Bout cards carry their corners on their own edges: red fades in from the
// left, blue from the right. Red is always the LEFT fighter (see
// resolveCorners), so a swap moves the fighters, never the colour.
const CORNER_GRADIENT =
    "bg-[linear-gradient(90deg,rgba(220,38,38,.38)_0%,transparent_32%,transparent_68%,rgba(37,99,235,.38)_100%)]";

// isPulledOutPair (a pulled-out fighter cancels the match) now lives in
// utils/end-event-readiness, next to the END EVENT readiness check.

/** manage-event's pairKey for two fighter ids (see getUniqueMatchedFighters). */
const pairKeyOf = (idA, idB) => [idA, idB].sort().join("-");

/**
 * The unique fighters behind a list of bout entries, in bout order — what the
 * Matched / Results views hand to the CSV export.
 * @param {Array<{pair: {fighterA: object, fighterB: object}}>} entries
 */
const fightersInPairs = (entries) => {
    const seen = new Set();
    const out = [];
    (entries ?? []).forEach((entry) => {
        [entry.pair?.fighterA, entry.pair?.fighterB].forEach((ef) => {
            if (!ef || seen.has(ef.fighterId)) return;
            seen.add(ef.fighterId);
            out.push(ef);
        });
    });
    return out;
};

export default function ManageEvent() {
    const navigate = useNavigate();
    const { id } = useParams();
    const [eventId, setEventId] = useState(id);

    // Roster filters / sort. Everything here is in-memory over the roster that
    // is already fetched below, and mirrored into the URL so a narrowed view is
    // deep-linkable (?tab=matched&entry=MMA&wc=D,E&sort=name&dir=desc).
    const [searchParams, setSearchParams] = useSearchParams();
    const urlTabRef = useRef(
        EVENT_FIGHTER_TABS.includes(searchParams.get("tab")) &&
            (id || searchParams.get("tab") !== "judges")
            ? searchParams.get("tab")
            : null
    );
    const [activeTab, setActiveTab] = useState(
        () => urlTabRef.current ?? "all"
    );
    const [filters, setFilters] = useState(() =>
        parseFiltersFromParams(searchParams)
    );
    const [sortKey, setSortKey] = useState(
        () => searchParams.get("sort") || defaultSortForTab(urlTabRef.current)
    );
    const [sortOrder, setSortOrder] = useState(() =>
        searchParams.get("dir") === "desc" ? "desc" : "asc"
    );
    const [matchedFighters, setMatchedFighters] = useState({});
    const [changed, setChanged] = useState(false);
    const [event, setEvent] = useState(null);
    const [eventFighters, setEventFighters] = useState(null);
    const [coverImageInputDragActive, setCoverImageInputDragActive] =
        useState(false);
    const [newCoverImage, setNewCoverImage] = useState(null);
    const [selectedEventFighter, setSelectedEventFighter] = useState(null);
    const [matchingEventFighter, setMatchingEventFighter] = useState(null);
    const [matchingChanged, setMatchingChanged] = useState(false);
    const [fighterSearchTerm, setFighterSearchTerm] = useState("");
    const [previewLiveStreamId, setPreviewLiveStreamId] = useState(null);
    // Judges and their scorecards, live. Feed the Judges tab and the decision
    // strip on each Matched bout card.
    const [eventJudges, setEventJudges] = useState(null);
    const [eventJudgesError, setEventJudgesError] = useState(false);
    const [scorecards, setScorecards] = useState([]);
    const judgeCount = eventJudges ? eventJudges.length : null;

    // Manual fighter registration states
    const [selectedFighterForAdd, setSelectedFighterForAdd] = useState(null);
    const [selectedEntryForAdd, setSelectedEntryForAdd] = useState(null);
    const [selectedEventWeightClassForAdd, setSelectedEventWeightClassForAdd] =
        useState(null);
    const [availableFighters, setAvailableFighters] = useState([]);
    const [fighterToRemove, setFighterToRemove] = useState(null);
    const [pullOutBlockedFighter, setPullOutBlockedFighter] = useState(null);
    const [tokenCountsByFighterId, setTokenCountsByFighterId] = useState({});
    const [fighterToRefund, setFighterToRefund] = useState(null);
    const [refunding, setRefunding] = useState(false);
    const [fighterToRejoin, setFighterToRejoin] = useState(null);
    const [rejoining, setRejoining] = useState(false);
    const [savingTestMode, setSavingTestMode] = useState(false);
    const [savingHoldScores, setSavingHoldScores] = useState(false);
    // Map of fighterId -> pending REGISTER_EVENT request id for this event.
    // Used to steer the admin to approve that specific request instead of
    // manually rejoining (which would make the approval fail).
    const [pendingRequestByFighterId, setPendingRequestByFighterId] = useState(
        () => new Map()
    );
    // Fighter ids with an undecided WEIGHT_CHANGE request for this event.
    const [pendingWeightChangeIds, setPendingWeightChangeIds] = useState(
        () => new Set()
    );
    // Drag state for reordering the running order of the card.
    const [draggingPairKey, setDraggingPairKey] = useState(null);
    const [dropTargetPairKey, setDropTargetPairKey] = useState(null);
    // Per-registration weight edit, scoped to this event only.
    const [weightEditFighter, setWeightEditFighter] = useState(null);
    const [weightEditClass, setWeightEditClass] = useState(null);
    const [savingWeight, setSavingWeight] = useState(false);

    const coverImageInputRef = useRef(null);
    const matchFighterModal = useRef(null);
    // The running order as stored, as far as this tab knows (see
    // persistBoutOrder), the write queue, and its generation.
    const storedBoutOrder = useRef([]);
    const boutOrderWrites = useRef(Promise.resolve());
    const boutOrderEpoch = useRef(0);

    // All tabs stay available even after the event ends — but on an ended
    // event the roster tabs are read-only history (see the per-tab handler
    // gating below). The public page still shows only "Event Ended" + results.
    const tabs = [
        {
            label: event?.isEnded ? "Registered" : "All",
            value: "all",
        },
        {
            label: event?.isEnded ? "Results" : "Matched",
            value: "matched",
        },
        {
            label: "Pulled Out",
            value: "pulledOut",
        },
        {
            label: "Refund Records",
            value: "refunded",
        },
        // Judges hang off the event id, so an unsaved event has no Judges tab.
        ...(eventId ? [{ label: "Judges", value: "judges" }] : []),
    ];

    // After an event ends, land the admin on the Results tab by default; the
    // other tabs remain reachable as read-only history.
    // ...unless the URL already asked for a specific tab (deep link).
    useEffect(() => {
        if (event?.isEnded && !urlTabRef.current) setActiveTab("matched");
    }, [event?.isEnded]);

    // The sort key the UI actually uses: falls back to the tab's default when
    // the stored key isn't valid there (e.g. "Height" then switching to Matched).
    const effectiveSort = useMemo(
        () =>
            isValidSortForTab(activeTab, sortKey)
                ? sortKey
                : defaultSortForTab(activeTab),
        [activeTab, sortKey]
    );

    const handleSortChange = (nextSort, nextOrder) => {
        setSortKey(nextSort);
        setSortOrder(nextOrder === "desc" ? "desc" : "asc");
    };

    const handleClearFilters = () => setFilters(emptyFilters());

    const filtersActive = hasActiveFilters(filters);

    // Keep the URL in sync with tab / filters / sort, omitting defaults.
    useEffect(() => {
        const params = filtersToParams(filters);
        if (activeTab !== "all") params.tab = activeTab;
        if (effectiveSort !== defaultSortForTab(activeTab)) {
            params.sort = effectiveSort;
        }
        if (sortOrder !== "asc") params.dir = sortOrder;
        // Don't touch history when nothing actually changed — the page also
        // has an unsaved-changes flow and a load effect we must not disturb.
        if (new URLSearchParams(params).toString() === searchParams.toString()) {
            return;
        }
        setSearchParams(params, { replace: true });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filters, activeTab, effectiveSort, sortOrder]);

    // Judges (tab label count, Judges tab, decision strips) and every judge's
    // scorecards for this event, kept live with snapshot listeners.
    useEffect(() => {
        if (!eventId) return undefined;
        setEventJudgesError(false);
        return subscribeEventJudges(eventId, setEventJudges, () =>
            setEventJudgesError(true)
        );
    }, [eventId]);
    useEffect(() => {
        if (!eventId) return undefined;
        return subscribeEventScorecards(eventId, setScorecards);
    }, [eventId]);
    // boutKey (= pairKey) -> { judgeId -> card }
    const scorecardsByBout = useMemo(() => {
        const map = {};
        scorecards.forEach((c) => {
            if (!map[c.boutKey]) map[c.boutKey] = {};
            map[c.boutKey][c.judgeId] = c;
        });
        return map;
    }, [scorecards]);

    // Load fighters who already have a PENDING REGISTER_EVENT request for this
    // event, so the Refund Records tab can show "approve in Requests" instead
    // of a Rejoin button.
    useEffect(() => {
        if (!eventId) return;
        (async () => {
            try {
                const snap = await getDocs(
                    query(
                        fighterRequestsCollection,
                        where("eventId", "==", eventId)
                    )
                );
                const map = new Map();
                // Same pass picks up undecided weight changes, which drive the
                // "Weight change" flag chip in the filter bar.
                const weightChanges = new Set();
                snap.forEach((d) => {
                    const x = d.data();
                    if (x?.status !== "PENDING") return;
                    if (x?.type === "REGISTER_EVENT") {
                        map.set(x.uid, d.id);
                    } else if (x?.type === "WEIGHT_CHANGE") {
                        weightChanges.add(x.uid);
                    }
                });
                setPendingRequestByFighterId(map);
                setPendingWeightChangeIds(weightChanges);
            } catch (e) {
                console.error("Failed to load pending requests:", e);
            }
        })();
    }, [eventId]);

    const handleClose = () => {
        if (window.history.length > 1) {
            navigate(-1);
        } else {
            navigate("/");
        }
    };

    const handleChange = (e) => {
        const name = e.target.name;
        const value = e.target.value;

        if (name === "coverImage") {
            if (e.target.files && e.target.files[0]) {
                const file = e.target.files[0];

                setNewCoverImage(file);
            }
            return;
        }

        if (!changed) {
            setChanged(true);
        }
        switch (name) {
            case "color":
                setEvent((prev) => {
                    return {
                        ...prev,
                        [name]: value?.toUpperCase()?.replaceAll(" ", ""),
                    };
                });
                break;
            case "event-year":
                const year = parseInt(value);
                if (!!year && year > 0 && year <= 9999) {
                    setEvent((prev) => {
                        const d = prev?.dateTime?.toDate() ?? new Date();
                        d.setFullYear(year);
                        return {
                            ...prev,
                            dateTime: Timestamp.fromDate(d),
                        };
                    });
                }
                break;
            case "event-month":
                const month = monthMap[value];
                if (month !== undefined) {
                    setEvent((prev) => {
                        const d = prev?.dateTime?.toDate() ?? new Date();
                        d.setMonth(month);

                        return {
                            ...prev,
                            dateTime: Timestamp.fromDate(d),
                        };
                    });
                }
                break;
            case "event-date":
                const date = parseInt(value);
                if (!!date && date > 0 && date <= 31) {
                    setEvent((prev) => {
                        const d = prev?.dateTime?.toDate() ?? new Date();
                        d.setDate(date);
                        return {
                            ...prev,
                            dateTime: Timestamp.fromDate(d),
                        };
                    });
                }
                break;
            case "event-hour":
                const hour = parseInt(value);
                if ((!!hour || hour === 0) && hour >= 0 && hour <= 23) {
                    setEvent((prev) => {
                        const d = prev?.dateTime?.toDate() ?? new Date();
                        d.setHours(hour);
                        return {
                            ...prev,
                            dateTime: Timestamp.fromDate(d),
                        };
                    });
                }
                break;
            case "event-minute":
                const minute = parseInt(value);
                if ((!!minute || minute === 0) && minute >= 0 && minute <= 59) {
                    setEvent((prev) => {
                        const d = prev?.dateTime?.toDate() ?? new Date();
                        d.setMinutes(minute);
                        return {
                            ...prev,
                            dateTime: Timestamp.fromDate(d),
                        };
                    });
                }
                break;

            case "expired-year":
                const expiredYear = parseInt(value);
                if (!!expiredYear && expiredYear > 0 && expiredYear <= 9999) {
                    setEvent((prev) => {
                        const d =
                            prev?.registerExpiredDateTime?.toDate() ??
                            new Date();
                        d.setFullYear(expiredYear);
                        return {
                            ...prev,
                            registerExpiredDateTime: Timestamp.fromDate(d),
                        };
                    });
                }
                break;
            case "expired-month":
                const expiredMonth = monthMap[value];
                if (expiredMonth !== undefined) {
                    setEvent((prev) => {
                        const d =
                            prev?.registerExpiredDateTime?.toDate() ??
                            new Date();
                        d.setMonth(expiredMonth);

                        return {
                            ...prev,
                            registerExpiredDateTime: Timestamp.fromDate(d),
                        };
                    });
                }
                break;
            case "expired-date":
                const expiredDate = parseInt(value);
                if (!!expiredDate && expiredDate > 0 && expiredDate <= 31) {
                    setEvent((prev) => {
                        const d =
                            prev?.registerExpiredDateTime?.toDate() ??
                            new Date();
                        d.setDate(expiredDate);
                        return {
                            ...prev,
                            registerExpiredDateTime: Timestamp.fromDate(d),
                        };
                    });
                }
                break;
            case "expired-hour":
                const expiredHour = parseInt(value);
                if (
                    (!!expiredHour || expiredHour === 0) &&
                    expiredHour >= 0 &&
                    expiredHour <= 23
                ) {
                    setEvent((prev) => {
                        const d =
                            prev?.registerExpiredDateTime?.toDate() ??
                            new Date();
                        d.setHours(expiredHour);
                        return {
                            ...prev,
                            registerExpiredDateTime: Timestamp.fromDate(d),
                        };
                    });
                }
                break;
            case "expired-minute":
                const expiredMinute = parseInt(value);
                if (
                    (!!expiredMinute || expiredMinute === 0) &&
                    expiredMinute >= 0 &&
                    expiredMinute <= 59
                ) {
                    setEvent((prev) => {
                        const d =
                            prev?.registerExpiredDateTime?.toDate() ??
                            new Date();
                        d.setMinutes(expiredMinute);
                        return {
                            ...prev,
                            registerExpiredDateTime: Timestamp.fromDate(d),
                        };
                    });
                }
                break;

            case "liveStreamUrl":
                // Legacy single-input; normalize to array and update first slot
                setEvent((prev) => {
                    const arr = getLiveStreamUrls(prev?.liveStreamUrl);
                    const next = [...(arr.length ? arr : [""])];
                    next[0] = value;
                    const firstValid = next.map((u) => getYouTubeVideoId(u)).find(Boolean);
                    setPreviewLiveStreamId(firstValid ?? null);
                    if (value && !getYouTubeVideoId(value)) {
                        toast.error("Invalid YouTube URL.");
                    }
                    return { ...prev, liveStreamUrl: next };
                });
                break;
            case "liveStreamTicketPurchaseDeadline-year":
                const liveStreamTicketPurchaseDeadlineYear = parseInt(value);
                if (
                    !!liveStreamTicketPurchaseDeadlineYear &&
                    liveStreamTicketPurchaseDeadlineYear > 0 &&
                    liveStreamTicketPurchaseDeadlineYear <= 9999
                ) {
                    setEvent((prev) => {
                        const d =
                            prev?.liveStreamTicketPurchaseDeadline?.toDate() ??
                            new Date();
                        d.setFullYear(liveStreamTicketPurchaseDeadlineYear);
                        return {
                            ...prev,
                            liveStreamTicketPurchaseDeadline:
                                Timestamp.fromDate(d),
                        };
                    });
                }
                break;
            case "liveStreamTicketPurchaseDeadline-month":
                const liveStreamTicketPurchaseDeadlineMonth = monthMap[value];
                if (liveStreamTicketPurchaseDeadlineMonth !== undefined) {
                    setEvent((prev) => {
                        const d =
                            prev?.liveStreamTicketPurchaseDeadline?.toDate() ??
                            new Date();
                        d.setMonth(liveStreamTicketPurchaseDeadlineMonth);

                        return {
                            ...prev,
                            liveStreamTicketPurchaseDeadline:
                                Timestamp.fromDate(d),
                        };
                    });
                }
                break;
            case "liveStreamTicketPurchaseDeadline-date":
                const liveStreamTicketPurchaseDeadlineDate = parseInt(value);
                if (
                    !!liveStreamTicketPurchaseDeadlineDate &&
                    liveStreamTicketPurchaseDeadlineDate > 0 &&
                    liveStreamTicketPurchaseDeadlineDate <= 31
                ) {
                    setEvent((prev) => {
                        const d =
                            prev?.liveStreamTicketPurchaseDeadline?.toDate() ??
                            new Date();
                        d.setDate(liveStreamTicketPurchaseDeadlineDate);
                        return {
                            ...prev,
                            liveStreamTicketPurchaseDeadline:
                                Timestamp.fromDate(d),
                        };
                    });
                }
                break;
            case "liveStreamTicketPurchaseDeadline-hour":
                const liveStreamTicketPurchaseDeadlineHour = parseInt(value);
                if (
                    (!!liveStreamTicketPurchaseDeadlineHour ||
                        liveStreamTicketPurchaseDeadlineHour === 0) &&
                    liveStreamTicketPurchaseDeadlineHour >= 0 &&
                    liveStreamTicketPurchaseDeadlineHour <= 23
                ) {
                    setEvent((prev) => {
                        const d =
                            prev?.liveStreamTicketPurchaseDeadline?.toDate() ??
                            new Date();
                        d.setHours(liveStreamTicketPurchaseDeadlineHour);
                        return {
                            ...prev,
                            liveStreamTicketPurchaseDeadline:
                                Timestamp.fromDate(d),
                        };
                    });
                }
                break;
            case "liveStreamTicketPurchaseDeadline-minute":
                const liveStreamTicketPurchaseDeadlineMinute = parseInt(value);
                if (
                    (!!liveStreamTicketPurchaseDeadlineMinute ||
                        liveStreamTicketPurchaseDeadlineMinute === 0) &&
                    liveStreamTicketPurchaseDeadlineMinute >= 0 &&
                    liveStreamTicketPurchaseDeadlineMinute <= 59
                ) {
                    setEvent((prev) => {
                        const d =
                            prev?.liveStreamTicketPurchaseDeadline?.toDate() ??
                            new Date();
                        d.setMinutes(liveStreamTicketPurchaseDeadlineMinute);
                        return {
                            ...prev,
                            liveStreamTicketPurchaseDeadline:
                                Timestamp.fromDate(d),
                        };
                    });
                }
                break;
            default:
                if (name?.startsWith("liveStreamUrl-")) {
                    const idx = parseInt(name.split("-")[1], 10);
                    if (!Number.isNaN(idx) && idx >= 0) {
                        setEvent((prev) => {
                            const arr = [...(Array.isArray(prev?.liveStreamUrl) ? prev.liveStreamUrl : [""])];
                            arr[idx] = value;
                            const firstValid = arr.map((u) => getYouTubeVideoId(u)).find(Boolean);
                            setPreviewLiveStreamId(firstValid ?? null);
                            if (value && !getYouTubeVideoId(value)) {
                                toast.error("Invalid YouTube URL.");
                            }
                            return { ...prev, liveStreamUrl: arr };
                        });
                    }
                } else {
                    setEvent((prev) => {
                        return {
                            ...prev,
                            [name]: value,
                        };
                    });
                }
                break;
        }
    };

    // Handle drag events to toggle dragActive state.
    const handleDrag = (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (e.type === "dragenter" || e.type === "dragover") {
            setCoverImageInputDragActive(true);
        } else if (e.type === "dragleave") {
            setCoverImageInputDragActive(false);
        }
    };

    // Handle file drop.
    const handleDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setCoverImageInputDragActive(false);
        if (e.dataTransfer.files && e.dataTransfer.files[0]) {
            const file = e.dataTransfer.files[0];
            const fileName = file?.name;
            const fileExtension = fileName?.split(".")?.pop();

            const allowFileExtention = ["png", "jpg", "jpeg"];

            if (
                !file?.type?.includes("image") ||
                !allowFileExtention.includes(fileExtension)
            ) {
                toast.error("Invalid file format.");
            } else {
                setNewCoverImage(file);
            }
        }
    };

    const handleCopyMatchedFighters = () => {
        // Group matched fighters by entry and weight class
        const groupedFighters = Object.values(matchedFighters).reduce(
            (groups, pair) => {
                const a = pair.fighterA;
                const b = pair.fighterB;

                // Check if both fighters have same entry and weight class
                const sameEntry = a.entry === b.entry;
                const weightClassA = getEventFighterWeightClass(a);
                const weightClassB = getEventFighterWeightClass(b);
                const sameWeightClass = weightClassA === weightClassB;

                if (sameEntry && sameWeightClass) {
                    const groupKey = `${a.entry} - ${weightClassesName[weightClassA]}`;
                    if (!groups[groupKey]) {
                        groups[groupKey] = [];
                    }
                    groups[groupKey].push(pair);
                } else {
                    if (!groups["Other"]) {
                        groups["Other"] = [];
                    }
                    groups["Other"].push(pair);
                }

                return groups;
            },
            {}
        );

        // Format the output string
        let outputString = "";

        // Add each group to the output string
        Object.entries(groupedFighters).forEach(([groupName, pairs]) => {
            outputString += `${groupName}\n`;

            pairs.forEach((pair) => {
                const a = pair.fighterA;
                const b = pair.fighterB;

                if (groupName === "Other") {
                    const weightClassA = getEventFighterWeightClass(a);
                    const weightClassB = getEventFighterWeightClass(b);
                    outputString += `${a.fighter.name} (${a.entry}) (${weightClassesName[weightClassA]}) vs ${b.fighter.name} (${b.entry}) (${weightClassesName[weightClassB]})\n`;
                } else {
                    const weightClassA = getEventFighterWeightClass(a);
                    outputString += `${a.fighter.name} vs ${b.fighter.name} (${weightClassesName[weightClassA]})\n`;
                }
            });

            outputString += "\n";
        });

        navigator.clipboard.writeText(outputString.trim());
        return toast.success("📋 Matched fighters copied to clipboard.");
    };

    /**
     * Export the given rows, in the order given — i.e. exactly what is on
     * screen. Called with no list it falls back to the whole roster sorted by
     * entry, which is what the button did before filtering existed.
     *
     * @param {Array} [list] event fighters, already filtered and sorted
     */
    const handleExportToCSV = (list) => {
        try {
            const source = Array.isArray(list)
                ? list
                : [...(eventFighters || [])].sort((a, b) =>
                      String(a.entry).localeCompare(String(b.entry))
                  );
            const csvData = source
                .filter((ef) => ef.fighter)
                .map((ef) => {
                    const fighter = ef.fighter ?? {};
                    const record = fighter?.records?.[ef.entry] ?? {};
                    return {
                        "Register On":
                            formatFirestoreTimestamp(ef.createdAt) ?? "",

                        Name: fighter.name ?? "",
                        "Match With": ef.matchWith
                            ? eventFighters.find(
                                (ef2) => ef2.fighterId === ef.matchWith
                            )?.fighter?.name
                            : "",
                        "Gym Name/Team Name": fighter?.gymNameOrTeamName ?? "",
                        Age: fighter?.age ?? "",
                        Nationality: fighter?.nationality ?? "",
                        "IC/Passport": fighter?.icOrPassport ?? "",
                        "Weight Class": (() => {
                            const eventWeightClass =
                                getEventFighterWeightClass(ef);
                            return eventWeightClass
                                ? `${eventWeightClass}: ${weightClasses[eventWeightClass]}`
                                : "";
                        })(),
                        "Height (cm)": fighter?.height ?? "",
                        Email: fighter?.email ?? "",
                        "Phone Number": fighter?.phoneNumber ?? "",
                        "Emergency Contact Number":
                            fighter?.emergencyContactNumber ?? "",
                        "IG Username": fighter?.igUsername ?? "",
                        Gender: genders[fighter?.gender?.toUpperCase()] ?? "",
                        "Walk Out Music": fighter?.walkOutMusic ?? "",

                        Entry: ef.entry ?? "",
                        Win: record?.win ?? 0,
                        Draw: record?.draw ?? 0,
                        Loss: record?.loss ?? 0,
                        TKO: fighter?.tko ?? "",
                        KO: fighter?.ko ?? "",
                        Submission: fighter?.submission ?? "",
                        Decision: fighter?.decision ?? "",

                        "Fighter Picture": fighter?.fighterPicture ?? "",
                        Receipt: ef.receipt ?? "",
                        "Pulled Out": ef.isPulledOut ? "Yes" : "No",
                    };
                });
            const csv = Papa.unparse(csvData);
            const blob = new Blob([csv], { type: "text/csv" });
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = `Fighter List - ${event?.name}.csv`;
            a.click();
            URL.revokeObjectURL(url);
            return toast.success("📋 Exported to CSV.");
        } catch (error) {
            console.error("Error on handleExportToCSV(): ", error);
            toast.error("Something went wrong when exporting to CSV.");
        }
    };

    const handleUploadEventCoverImage = async () => {
        try {
            if (!eventId) {
                return toast.error(
                    "Create the event before upload the cover image."
                );
            }
            if (!!newCoverImage) {
                await toast.promise(
                    uploadEventCoverImage(newCoverImage, eventId),
                    {
                        success: (downloadURL) => {
                            setNewCoverImage(null);
                            setEvent((prev) => {
                                return {
                                    ...prev,
                                    coverImage: downloadURL,
                                };
                            });
                            return "Event Cover Image uploaded successful.";
                        },
                        error: (err) => {
                            console.error(
                                "Error in handleUploadEventCoverImage: ",
                                err
                            );
                            return "Something went wrong when update the Event Cover Image.";
                        },
                        loading: "Uploading Cover Image",
                    }
                );
            }
        } catch (error) {
            console.error(error);
            toast.error(
                "Something went wrong when uploading new Fighter Picture."
            );
        }
    };

    const handleSubmit = useCallback(async () => {
        try {
            const submitEvent = {
                ...event,
            };

            // Filter empty/whitespace live stream URLs and store as array
            const urls = (event?.liveStreamUrl ?? [])
                .map((s) => (s != null ? String(s).trim() : ""))
                .filter(Boolean);
            submitEvent.liveStreamUrl = urls.length > 0 ? urls : null;

            if (!!event?.liveStreamTicketPrice) {
                const price = parseFloat(event?.liveStreamTicketPrice);
                if (isNaN(price)) {
                    toast.error("Invalid price.");
                    return;
                }

                submitEvent.liveStreamTicketPrice = price;
            }

            if (
                !!event?.registerExpiredDateTime &&
                event?.registerExpiredDateTime?.toDate() >
                event.dateTime?.toDate()
            ) {
                return toast.error(
                    "Register Expired Date Time must be less than Event Date Time."
                );
            }

            if (!!eventId) {
                toast.promise(updateEvent(eventId, submitEvent), {
                    success: "Update Event Info Successful",
                    error: (error) => {
                        console.error("Error on handleSubmit(): ", error);
                        return `Something went wrong when Update Event Info`;
                    },
                    loading: "Updating",
                });
            } else {
                toast.promise(createEvent(submitEvent), {
                    success: (data) => {
                        setEventId(data?.id);
                        navigate(`${MANAGE_EVENT_PATH}/${data?.id}`);
                        return "Create Event Successful";
                    },
                    error: (error) => {
                        console.error("Error on handleSubmit(): ", error);
                        return `Something went wrong when Creating Event Info`;
                    },
                    loading: "Creating",
                });
            }
        } catch (error) {
            console.error(error);
        }
    }, [event, eventId, navigate]);

    const handleMatchEventFighter = async () => {
        try {
            if (!selectedEventFighter || !matchingEventFighter) return;
            await toast.promise(
                matchEventFighter(selectedEventFighter, matchingEventFighter),
                {
                    success: () => {
                        setEventFighters((prev) => {
                            // Make a copy
                            const newEventFighters = [...prev];
                            if (!!selectedEventFighter?.matchWith) {
                                const c = newEventFighters.find(
                                    (ef) =>
                                        ef.fighterId ===
                                        selectedEventFighter.matchWith
                                );
                                if (c) c.matchWith = null;
                            }
                            if (!!matchingEventFighter?.matchWith) {
                                const d = newEventFighters.find(
                                    (ef) =>
                                        ef.fighterId ===
                                        matchingEventFighter.matchWith
                                );
                                if (d) d.matchWith = null;
                            }
                            const a = newEventFighters.find(
                                (ef) => ef.id === selectedEventFighter.id
                            );
                            if (a)
                                a.matchWith = matchingEventFighter?.fighterId;
                            const b = newEventFighters.find(
                                (ef) => ef.id === matchingEventFighter.id
                            );
                            if (b)
                                b.matchWith = selectedEventFighter?.fighterId;
                            return newEventFighters;
                        });
                        return "Matched!";
                    },
                    error: (error) => {
                        console.error(
                            "Error on handleMatchEventFighter(): ",
                            error
                        );
                        return "Something went wrong when matching fighter.";
                    },
                    loading: "Matching...",
                }
            );
        } catch (error) {
            console.error(error);
        }
    };

    const handleUnMatchEventFighter = async () => {
        try {
            if (!selectedEventFighter || !matchingEventFighter) return;
            await toast.promise(
                unMatchEventFighter(selectedEventFighter, matchingEventFighter),
                {
                    success: () => {
                        setEventFighters((prev) => {
                            // Make a copy
                            const newEventFighters = [...prev];
                            const a = newEventFighters.find(
                                (ef) => ef?.id === selectedEventFighter?.id
                            );
                            if (a) a.matchWith = null;
                            const b = newEventFighters.find(
                                (ef) => ef?.id === matchingEventFighter?.id
                            );
                            if (b) b.matchWith = null;
                            return newEventFighters;
                        });
                        return "Unmatched!";
                    },
                    error: (error) => {
                        console.error(
                            "Error on handleUnMatchEventFighter(): ",
                            error
                        );
                        return "Something went wrong when unmatching fighter.";
                    },
                    loading: "Unmatching...",
                }
            );
        } catch (error) {
            console.error(error);
        }
    };

    const handleMatchingFighter = (eventFighter) => {
        setSelectedEventFighter(eventFighter);
        if (!!eventFighter?.matchWith) {
            const matchingFighter = eventFighters.find(
                (ef) => ef.fighterId === eventFighter.matchWith
            );
            setMatchingEventFighter(matchingFighter);
        }
        // matchFighterModal?.current.showModal();
    };

    const handleEndMatchingFighter = () => {
        setSelectedEventFighter(null);
        setMatchingEventFighter(null);
        setMatchingChanged(false);
    };

    const handleSelectMathingEventFighter = (eventFighter) => {
        if (!matchingChanged) {
            setMatchingChanged(true);
        }
        setMatchingEventFighter(eventFighter);
    };

    const handleSetWinBy = (pairKey, winBy) => {
        setMatchedFighters((prev) => {
            const newMatchedFighters = { ...prev };
            const matchedFighter = newMatchedFighters[pairKey];
            matchedFighter.winBy = winBy;
            matchedFighter.isDraw = false;
            return newMatchedFighters;
        });
    };

    const handleSetWinner = (pairKey, winnerId, loserId) => {
        setMatchedFighters((prev) => {
            const newMatchedFighters = { ...prev };
            const matchedFighter = newMatchedFighters[pairKey];
            matchedFighter.winId = winnerId;
            matchedFighter.lossId = loserId;
            matchedFighter.isDraw = false;
            return newMatchedFighters;
        });
    };

    const handleSetDraw = (pairKey) => {
        setMatchedFighters((prev) => {
            const newMatchedFighters = { ...prev };
            const matchedFighter = newMatchedFighters[pairKey];
            matchedFighter.isDraw = true;
            matchedFighter.winId = null;
            matchedFighter.lossId = null;
            matchedFighter.winBy = null;
            return newMatchedFighters;
        });
    };

    // The judges' suggestion, applied through the same handlers as the WIN /
    // DRAW / TKO / KO / DECISION buttons. Nothing is saved here: the result is
    // recorded by the usual End Event flow.
    const handleAcceptJudges = (pairKey, { isDraw, winnerId, loserId, winBy }) => {
        if (isDraw) {
            handleSetDraw(pairKey);
        } else {
            handleSetWinner(pairKey, winnerId, loserId);
            handleSetWinBy(pairKey, winBy);
        }
        toast.success("Judges' result filled in. End the event to record it.");
    };

    // Every bout at once: fill WIN / DRAW + method from the judges' cards,
    // the same as pressing ACCEPT on each. A pick the admin already made by
    // hand is never overwritten; bouts without a clear result are skipped.
    // Nothing is saved: the admin checks the card, then presses END EVENT.
    const handleFillFromJudges = () => {
        const filled = []; // bout numbers, for the toast
        let already = 0;
        let kept = 0;
        let waiting = 0;
        let disagree = 0;
        orderedPairKeys.forEach((pairKey, index) => {
            const pair = matchedFighters[pairKey];
            if (!pair) return;
            // A pulled-out fighter cancels the match: nothing to fill.
            if (isPulledOutPair(pair)) return;
            const isMma = isMmaPair(pair.fighterA, pair.fighterB);
            const suggestion = judgeSuggestion(
                eventJudges ?? [],
                scorecardsByBout[pairKey],
                pair.fighterA?.fighterId,
                pair.fighterB?.fighterId,
                pair,
                { isMma }
            );
            if (!suggestion) {
                if (boutVerdict(eventJudges ?? [], scorecardsByBout[pairKey], { isMma }).status === "conflict") disagree += 1;
                else waiting += 1;
                return;
            }
            if (suggestion.applied) {
                already += 1;
                return;
            }
            if (hasResult(pair)) {
                kept += 1;
                return;
            }
            if (suggestion.isDraw) {
                handleSetDraw(pairKey);
            } else {
                handleSetWinner(pairKey, suggestion.winnerId, suggestion.loserId);
                handleSetWinBy(pairKey, suggestion.winBy);
            }
            filled.push(index + 1);
        });

        const notes = [
            already && `${already} already set`,
            kept && `${kept} kept as you picked`,
            waiting && `${waiting} still waiting for cards`,
            disagree && `${disagree} where judges disagree`,
        ].filter(Boolean);
        const tail = notes.length ? ` (${notes.join(", ")})` : "";
        if (filled.length > 0) {
            toast.success(
                `Filled ${filled.length === 1 ? "match" : "matches"} ${filled.join(", ")} from the judges' cards${tail}. Check them, then press END EVENT.`,
                // Top: the page's toasts sit bottom-right, right over this
                // button and END EVENT, which the admin reaches for next.
                { duration: 6000, position: "top-center" }
            );
        } else {
            toast(`Nothing new to fill${tail}.`, { duration: 5000, position: "top-center" });
        }
    };

    // Swap red (left) and blue (right) for one bout. The server refuses once
    // any judge has a card for it; the button is locked in that case too.
    const [swappingPairKey, setSwappingPairKey] = useState(null);
    const handleSwapCorners = async (pairKey) => {
        if (!eventId || swappingPairKey) return;
        setSwappingPairKey(pairKey);
        try {
            const res = await swapBoutCorners(eventId, pairKey);
            if (res?.success) {
                setEvent((prev) => ({
                    ...prev,
                    boutCorners: {
                        ...(prev?.boutCorners || {}),
                        [pairKey]: res.redFighterId,
                    },
                }));
                toast.success("Corners swapped.");
            } else {
                toast.error(res?.error || "Couldn't swap corners.");
            }
        } catch (error) {
            console.error("swapBoutCorners failed:", error);
            toast.error("Couldn't swap corners.");
        } finally {
            setSwappingPairKey(null);
        }
    };

    const ticketUrlInput = (
        <div className="input input-bordered w-full p-0 flex flex-row">
            <input
                type="text"
                className="px-4 w-full"
                name="ticketUrl"
                id="ticketUrl"
                placeholder="E.g: https://ticket2u.com.my/event/dashboard/xxxxx"
                value={event?.ticketUrl ?? ""}
                onChange={handleChange}
            />
            <a
                href={event?.ticketUrl ?? "#ticketUrl"}
                target={!!event?.ticketUrl ? "_blank" : "_self"}
                rel="noreferrer"
                className="btn btn-square btn-ghost"
            >
                <LinkIcon size={18} />
            </a>
        </div>
    );

    const liveStreamUrls = Array.isArray(event?.liveStreamUrl)
        ? event.liveStreamUrl
        : [""];
    const liveStreamUrlInput = (
        <div className="flex flex-col gap-2 w-full">
            {liveStreamUrls.map((url, i) => {
                const videoId = getYouTubeVideoId(url);
                return (
                    <div
                        key={i}
                        className="input input-bordered w-full p-0 flex flex-row"
                    >
                        <input
                            type="text"
                            className="px-4 w-full"
                            name={`liveStreamUrl-${i}`}
                            id={`liveStreamUrl-${i}`}
                            placeholder="E.g: https://youtube.com/live/xxxxxx"
                            value={url ?? ""}
                            onChange={handleChange}
                        />
                        <div className="btn btn-square btn-ghost relative group flex-shrink-0">
                            {videoId ? (
                                <CircleCheck />
                            ) : url?.trim() && !videoId ? (
                                <CircleX />
                            ) : (
                                <CircleHelp />
                            )}
                            <div className="absolute -top-0 -translate-y-full right-0 bg-base-100 border p-2 opacity-0 hidden group-hover:block group-hover:opacity-100 group-hover:-translate-y-[110%] shadow-md transition-all z-10">
                                {videoId ? (
                                    <div className="w-[20rem] aspect-video max-w-[90vw] flex-center">
                                        <VideoPlayer
                                            videoId={videoId}
                                            showController={false}
                                            className="relative w-full h-full"
                                        />
                                    </div>
                                ) : url?.trim() && !videoId ? (
                                    <p className="text-nowrap">
                                        Live Stream URL is invalid.
                                    </p>
                                ) : (
                                    <p className="text-nowrap">
                                        Live Stream URL is missing.
                                    </p>
                                )}
                            </div>
                        </div>
                        <button
                            type="button"
                            className="btn btn-square btn-ghost flex-shrink-0"
                            onClick={() => {
                                setEvent((prev) => {
                                    const arr = [
                                        ...(Array.isArray(prev?.liveStreamUrl)
                                            ? prev.liveStreamUrl
                                            : [""]),
                                    ];
                                    arr.splice(i, 1);
                                    const next = arr.length ? arr : [""];
                                    const firstValid = next
                                        .map((u) => getYouTubeVideoId(u))
                                        .find(Boolean);
                                    setPreviewLiveStreamId(firstValid ?? null);
                                    return { ...prev, liveStreamUrl: next };
                                });
                            }}
                            aria-label="Remove URL"
                        >
                            <X size={18} />
                        </button>
                    </div>
                );
            })}
            <button
                type="button"
                className="btn btn-outline w-full"
                onClick={() => {
                    setEvent((prev) => ({
                        ...prev,
                        liveStreamUrl: [
                            ...(Array.isArray(prev?.liveStreamUrl)
                                ? prev.liveStreamUrl
                                : [""]),
                            "",
                        ],
                    }));
                }}
            >
                <Plus size={18} className="mr-1" />
                Add URL
            </button>
        </div>
    );

    const liveStreamTicketPriceInput = (
        <div className="w-full flex flex-row gap-2 items-center">
            <label
                htmlFor="liveStreamTicketPrice"
                className="input input-bordered w-full flex flex-row items-center"
            >
                <span className="opacity-80 pr-2">MYR</span>
                <div className="h-full divider divider-horizontal m-0" />
                <input
                    step="1"
                    type="number"
                    className="w-full"
                    name="liveStreamTicketPrice"
                    id="liveStreamTicketPrice"
                    placeholder="Live Stream Ticket Price"
                    value={event?.liveStreamTicketPrice ?? "0.00"}
                    onChange={handleChange}
                />
            </label>
        </div>
    );

    const nameInput = (
        <input
            type="text"
            name="name"
            id="name"
            className="input input-bordered w-full"
            onChange={handleChange}
            placeholder="Event Name"
            value={event?.name ?? ""}
        />
    );

    const detailsInput = (
        <textarea
            rows={4}
            type="text"
            name="details"
            id="details"
            className="textarea textarea-bordered w-full"
            onChange={handleChange}
            placeholder="Event Details"
            value={event?.details ?? ""}
        />
    );

    const dateInput = (
        <div className="w-full flex flex-row gap-2 items-center">
            <input
                type="number"
                className="input input-bordered w-full"
                placeholder="Date"
                name="event-date"
                id="event-date"
                defaultValue={extractTimestamp(event?.dateTime)?.day}
                min={1}
                max={31}
                onChange={handleChange}
            />
            <p>/</p>
            <SelectInput
                options={months}
                placeholder="Month"
                onChange={handleChange}
                name="event-month"
                onSelect={(m) => {
                    setEvent((prev) => {
                        const dateTime = prev.dateTime.toDate();
                        const monthIndex = monthMap[m];
                        if (!!monthMap) {
                            dateTime.setMonth(monthIndex);
                            return {
                                ...prev,
                                dateTime: Timestamp.fromDate(dateTime),
                            };
                        }
                    });
                }}
                defaultValue={extractTimestamp(event?.dateTime)?.month ?? ""}
                value={extractTimestamp(event?.dateTime)?.month ?? ""}
            />
            <p>/</p>
            <input
                type="number"
                className="input input-bordered w-full"
                placeholder="Year"
                name="event-year"
                id="event-year"
                defaultValue={extractTimestamp(event?.dateTime)?.year}
                min={1}
                max={9999}
                onChange={handleChange}
            />
        </div>
    );
    const timeInput = (
        <div className="w-full flex flex-row gap-2 items-center">
            <input
                type="number"
                className="input input-bordered w-full"
                placeholder="Hour"
                name="event-hour"
                id="event-hour"
                defaultValue={extractTimestamp(event?.dateTime)?.hour}
                onChange={handleChange}
                min={0}
                max={23}
            />
            <p>:</p>
            <input
                type="number"
                className="input input-bordered w-full"
                placeholder="Minute"
                name="event-minute"
                id="event-minute"
                defaultValue={extractTimestamp(event?.dateTime)?.minute}
                onChange={handleChange}
                min={0}
                max={59}
            />
        </div>
    );
    // const statusInput = (
    //   <select name="status" id="status" className='select select-bordered w-full' defaultValue={event?.status ?? undefined} onChange={handleChange}>
    //     <option value={undefined} defaultChecked disabled>Please select a Status</option>
    //     <option value={"I"} >Inactive</option>
    //     <option value={"A"} >Active</option>
    //   </select>
    // )
    const statusInput = (
        <div className="flex flex-row gap-4 w-full">
            <button
                onClick={() => {
                    handleChange({ target: { name: "status", value: "A" } });
                }}
                className={twMerge(
                    "btn btn-outline flex-1",
                    event?.status === "A" ? "btn-primary" : ""
                )}
            >
                ACTIVE
            </button>
            <button
                onClick={() => {
                    handleChange({ target: { name: "status", value: "I" } });
                }}
                className={twMerge(
                    "btn btn-outline flex-1",
                    event?.status === "I" ? "btn-primary" : ""
                )}
            >
                INACTIVE
            </button>
        </div>
    );

    // A new event keeps the flag with the rest of the form; an existing one
    // saves it at once so the server's blocks never wait on the Save button.
    const handleToggleTestMode = async (e) => {
        const next = e.target.checked;
        if (!eventId) {
            setChanged(true);
            setEvent((prev) => ({ ...prev, isTest: next }));
            return;
        }
        setSavingTestMode(true);
        try {
            await setEventTestMode(eventId, next);
            setEvent((prev) => ({ ...prev, isTest: next }));
            toast.success(
                next ? "Marked as a test event" : "No longer a test event"
            );
        } catch (error) {
            console.error("Error on handleToggleTestMode(): ", error);
            toast.error("Couldn't change Test event. Try again.");
        } finally {
            setSavingTestMode(false);
        }
    };
    const testModeInput = (
        <label
            htmlFor="isTest"
            className={twMerge(
                "flex flex-row gap-4 items-start border p-4 cursor-pointer",
                event?.isTest ? "border-warning" : ""
            )}
        >
            <input
                type="checkbox"
                id="isTest"
                name="isTest"
                className="toggle toggle-warning mt-1"
                checked={!!event?.isTest}
                onChange={handleToggleTestMode}
                disabled={!!event?.isEnded || savingTestMode}
            />
            <div className="flex flex-col gap-1">
                <p className="font-bold">Test event</p>
                <p className="text-sm opacity-70">
                    For practice with the judges. Hidden from the home page.
                    Fighters can't register and tickets can't be bought. END
                    EVENT, Refund, Rejoin with token and approving
                    registrations are blocked, so no real fighter's record or
                    credit changes.
                </p>
            </div>
        </label>
    );

    // Partner Data API: live round scores are published by default. On,
    // every match's round scores stay hidden from the partner until each
    // active judge has submitted. Saved at once, like Test event.
    const handleToggleHoldScores = async (e) => {
        const next = e.target.checked;
        setSavingHoldScores(true);
        try {
            await setEventPartnerHoldScores(eventId, next);
            setEvent((prev) => ({ ...prev, partnerHoldScores: next }));
            toast.success(
                next
                    ? "Partner scores held until each match is scored"
                    : "Partner scores published live"
            );
        } catch (error) {
            console.error("Error on handleToggleHoldScores(): ", error);
            toast.error("Couldn't change the partner score setting. Try again.");
        } finally {
            setSavingHoldScores(false);
        }
    };
    const holdScoresInput = eventId ? (
        <label
            htmlFor="partnerHoldScores"
            className={twMerge(
                "flex flex-row gap-4 items-start border p-4 cursor-pointer",
                event?.partnerHoldScores ? "border-info" : ""
            )}
        >
            <input
                type="checkbox"
                id="partnerHoldScores"
                name="partnerHoldScores"
                className="toggle toggle-info mt-1"
                checked={!!event?.partnerHoldScores}
                onChange={handleToggleHoldScores}
                disabled={!!event?.isEnded || savingHoldScores}
            />
            <div className="flex flex-col gap-1">
                <p className="font-bold">Hold partner scores</p>
                <p className="text-sm opacity-70">
                    Partner API: round scores are published live, round by
                    round, as judges press NEXT ROUND. Turn this on to hide
                    each match's scores from the partner until every active
                    judge has submitted. Matches, statuses and the official
                    result are unaffected.
                </p>
            </div>
        </label>
    ) : null;

    const registerExpiredDateInput = (
        <div className="w-full flex flex-row gap-2 items-center">
            <input
                type="number"
                className="input input-bordered w-full"
                placeholder="Date"
                name="expired-date"
                id="expired-date"
                defaultValue={
                    extractTimestamp(event?.registerExpiredDateTime)?.day
                }
                min={1}
                max={31}
                onChange={handleChange}
            />
            <p>/</p>
            <SelectInput
                options={months}
                placeholder="Month"
                onChange={handleChange}
                name="expired-month"
                id="expired-month"
                onSelect={(m) => {
                    setEvent((prev) => {
                        const registerExpiredDateTime =
                            prev?.registerExpiredDateTime?.toDate() ??
                            new Date();
                        const monthIndex = monthMap[m];
                        if (!!monthMap) {
                            registerExpiredDateTime.setMonth(monthIndex);
                            return {
                                ...prev,
                                registerExpiredDateTime: Timestamp.fromDate(
                                    registerExpiredDateTime
                                ),
                            };
                        }
                    });
                }}
                defaultValue={
                    extractTimestamp(event?.registerExpiredDateTime)?.month ??
                    ""
                }
                value={
                    extractTimestamp(event?.registerExpiredDateTime)?.month ??
                    ""
                }
            />
            <p>/</p>
            <input
                type="number"
                className="input input-bordered w-full"
                placeholder="Year"
                name="expired-year"
                id="expired-year"
                defaultValue={
                    extractTimestamp(event?.registerExpiredDateTime)?.year
                }
                min={1}
                max={9999}
                onChange={handleChange}
            />
        </div>
    );
    const registerExpiredTimeInput = (
        <div className="w-full flex flex-row gap-2 items-center">
            <input
                type="number"
                className="input input-bordered w-full"
                placeholder="Hour"
                name="expired-hour"
                id="expired-hour"
                defaultValue={
                    extractTimestamp(event?.registerExpiredDateTime)?.hour
                }
                onChange={handleChange}
                min={0}
                max={23}
            />
            <p>:</p>
            <input
                type="number"
                className="input input-bordered w-full"
                placeholder="Minute"
                name="expired-minute"
                id="expired-minute"
                defaultValue={
                    extractTimestamp(event?.registerExpiredDateTime)?.minute
                }
                onChange={handleChange}
                min={0}
                max={59}
            />
        </div>
    );

    const venueInput = (
        <div className="w-full flex-center gap-2">
            <input
                type="text"
                className="input input-bordered w-full"
                name="venue"
                id="venue"
                placeholder="Venue Name"
                value={event?.venue ?? ""}
                onChange={handleChange}
            />
            <input
                type="text"
                className="input input-bordered w-full"
                name="address"
                id="address"
                placeholder="Address"
                value={event?.address ?? ""}
                onChange={handleChange}
            />

            <div className="input input-bordered w-full p-0 flex flex-row">
                <input
                    type="text"
                    className="px-4 w-full"
                    name="googleMap"
                    id="googleMap"
                    placeholder="Google Map URL"
                    value={event?.googleMap ?? ""}
                    onChange={handleChange}
                />
                <a
                    href={event?.googleMap ?? "#googleMap"}
                    target={!!event?.googleMap ? "_blank" : "_self"}
                    rel="noreferrer"
                >
                    <img
                        src="/assets/img/logo-googlemaps.svg"
                        className="btn btn-square btn-ghost p-3"
                        alt="Google Map"
                    />
                </a>
            </div>

            <div className="input input-bordered w-full p-0 flex flex-row">
                <input
                    type="text"
                    className="px-4 w-full"
                    name="waze"
                    id="waze"
                    placeholder="Waze URL"
                    value={event?.waze ?? ""}
                    onChange={handleChange}
                />
                <a
                    href={event?.waze ?? "#waze"}
                    target={!!event?.waze ? "_blank" : "_self"}
                    rel="noreferrer"
                >
                    <img
                        src="/assets/img/logo-waze.svg"
                        className="btn btn-square btn-ghost p-3"
                        alt="Waze"
                    />
                </a>
            </div>
        </div>
    );

    const colorInput = (
        <div className="input input-bordered w-full flex flex-row p-0">
            <p className="h-full aspect-square flex-center">#</p>
            <input
                type="text"
                className="w-full"
                placeholder="Event Color Code (FFFFFF)"
                name="color"
                id="color"
                value={event?.color ?? ""}
                onChange={handleChange}
            />
            <div
                className="h-full aspect-square flex-center"
                style={{
                    backgroundColor: `#${event?.color && event?.color?.length === 6
                        ? event?.color
                        : "FFFFFF"
                        }`,
                }}
            >
                {!(event?.color && event?.color?.length === 6) ? (
                    <p className="text-error font-bold text-lg">?</p>
                ) : null}
            </div>
        </div>
    );
    const createdAtInput = (
        <input
            type="text"
            className="input input-bordered w-full"
            defaultValue={
                formatFirestoreTimestamp(event?.createdAt) ?? "Not Available"
            }
            placeholder="Not Available"
            disabled
            readOnly
        />
    );
    const updatedAtInput = (
        <input
            type="text"
            className="input input-bordered w-full"
            defaultValue={
                formatFirestoreTimestamp(event?.updateAt) ?? "Not Available"
            }
            placeholder="Not Available"
            disabled
            readOnly
        />
    );

    const handleEndEvent = () => {
        if (event?.isTest) {
            toast.error("This is a test event. Turn off Test event first.");
            return;
        }
        // The button is disabled until every bout has a result and a method
        // (see utils/end-event-readiness); never open the dialog without.
        if (!endEventReadiness(matchedFighters).ready) return;
        try {
            const dialog = document.getElementById("end-event-dialog");
            dialog.showModal();
        } catch (error) {
            console.error("Error on handleEndEvent(): ", error);
        }
    };

    const handleConfirmEndEvent = async () => {
        try {
            toast.promise(
                (async () => {
                    // Pulled-out pairs are cancelled matches: no result is
                    // recorded for them (the server skips them too).
                    const result = await updateRecords({
                        matchedFighters: Object.fromEntries(
                            Object.entries(matchedFighters).filter(
                                ([, pair]) => !isPulledOutPair(pair)
                            )
                        ),
                        eventId: eventId,
                    });
                    if (!result.data || result.data.error) {
                        throw new Error(
                            result.data?.error || "Something went wrong."
                        );
                    }
                    return result;
                })(),
                {
                    loading: "Ending event...",
                    success: () => {
                        // Update local event state to reflect ended status
                        setEvent((prev) => ({
                            ...prev,
                            isEnded: true,
                            endedAt: new Date(),
                            endedBy: "current_user", // This will be set by the server
                        }));
                        return "Event ended successfully.";
                    },
                    error: (error) => {
                        console.error(
                            "Error on handleConfirmEndEvent(): ",
                            error
                        );
                        return error.message || "Something went wrong.";
                    },
                }
            );
        } catch (error) {
            console.error("Error on handleConfirmEndEvent(): ", error);
        }
    };

    const liveStreamTicketPurchaseDeadlineDateInput = (
        <div className="w-full flex flex-row gap-2 items-center">
            <input
                type="number"
                className="input input-bordered w-full"
                placeholder="Date"
                name="liveStreamTicketPurchaseDeadline-date"
                id="liveStreamTicketPurchaseDeadline-date"
                defaultValue={
                    extractTimestamp(event?.liveStreamTicketPurchaseDeadline)
                        ?.day
                }
                min={1}
                max={31}
                onChange={handleChange}
            />
            <p>/</p>
            <SelectInput
                options={months}
                placeholder="Month"
                onChange={handleChange}
                name="liveStreamTicketPurchaseDeadline-month"
                id="liveStreamTicketPurchaseDeadline-month"
                onSelect={(m) => {
                    setEvent((prev) => {
                        const d =
                            prev?.liveStreamTicketPurchaseDeadline?.toDate() ??
                            new Date();
                        const monthIndex = monthMap[m];
                        if (!!monthMap) {
                            d.setMonth(monthIndex);
                            return {
                                ...prev,
                                liveStreamTicketPurchaseDeadline:
                                    Timestamp.fromDate(d),
                            };
                        }
                    });
                }}
                defaultValue={
                    extractTimestamp(event?.liveStreamTicketPurchaseDeadline)
                        ?.month ?? ""
                }
                value={
                    extractTimestamp(event?.liveStreamTicketPurchaseDeadline)
                        ?.month ?? ""
                }
            />
            <p>/</p>
            <input
                type="number"
                className="input input-bordered w-full"
                placeholder="Year"
                name="liveStreamTicketPurchaseDeadline-year"
                id="liveStreamTicketPurchaseDeadline-year"
                defaultValue={
                    extractTimestamp(event?.liveStreamTicketPurchaseDeadline)
                        ?.year
                }
                min={1}
                max={9999}
                onChange={handleChange}
            />
        </div>
    );
    const liveStreamTicketPurchaseDeadlineTimeInput = (
        <div className="w-full flex flex-row gap-2 items-center">
            <input
                type="number"
                className="input input-bordered w-full"
                placeholder="Hour"
                name="liveStreamTicketPurchaseDeadline-hour"
                id="liveStreamTicketPurchaseDeadline-hour"
                defaultValue={
                    extractTimestamp(event?.liveStreamTicketPurchaseDeadline)
                        ?.hour
                }
                onChange={handleChange}
                min={0}
                max={23}
            />
            <p>:</p>
            <input
                type="number"
                className="input input-bordered w-full"
                placeholder="Minute"
                name="liveStreamTicketPurchaseDeadline-minute"
                id="liveStreamTicketPurchaseDeadline-minute"
                defaultValue={
                    extractTimestamp(event?.liveStreamTicketPurchaseDeadline)
                        ?.minute
                }
                onChange={handleChange}
                min={0}
                max={59}
            />
        </div>
    );

    useEffect(() => {
        (async () => {
            if (!!eventId) {
                const eventSnapsnot = await getDoc(
                    doc(eventsCollection, eventId)
                );
                if (eventSnapsnot.exists()) {
                    const data = eventSnapsnot.data();
                    storedBoutOrder.current = data?.boutOrder ?? [];
                    const urls = getLiveStreamUrls(data?.liveStreamUrl);
                    setEvent({
                        ...data,
                        id: eventSnapsnot.id,
                        liveStreamUrl: urls.length ? urls : [""],
                    });
                    const firstUrl = urls[0];
                    setPreviewLiveStreamId(
                        firstUrl ? getYouTubeVideoId(firstUrl) : null
                    );

                    const q = query(
                        eventFightersCollection,
                        where("eventId", "==", eventId)
                    );

                    const eventFightersSnapshot = await getDocs(q);

                    const fetchEventFighterPromises = [];
                    eventFightersSnapshot.forEach((snapshot) => {
                        const eventFighter = snapshot.data();
                        const eventFighterId = snapshot.id;
                        fetchEventFighterPromises.push(
                            new Promise(async (res, rej) => {
                                const fighterSnapsnot = await getDoc(
                                    doc(
                                        fightersCollection,
                                        eventFighter.fighterId
                                    )
                                );
                                if (fighterSnapsnot.exists()) {
                                    const fighterData = fighterSnapsnot.data();
                                    // Filter out deleted fighters
                                    if (!fighterData.deletedAt) {
                                        res({
                                            ...eventFighter,
                                            id: eventFighterId,
                                            fighter: {
                                                ...fighterData,
                                                id: fighterSnapsnot.id,
                                            },
                                        });
                                    } else {
                                        res(null);
                                    }
                                } else {
                                    res({
                                        ...eventFighter,
                                        id: eventFighterId,
                                        fighter: null,
                                    });
                                }
                            })
                        );
                    });
                    setEventFighters(
                        eventFightersSnapshot.docs.map((d) => ({
                            ...d.data(),
                            id: d.id,
                        }))
                    );

                    const fetchedEventFighters = await Promise.all(
                        fetchEventFighterPromises
                    );
                    // Filter out deleted fighters and null results
                    setEventFighters(
                        fetchedEventFighters.filter((ef) => ef && !!ef.fighter)
                    );
                } else {
                    navigate("/404");
                    setEvent({});
                    toast.error("Something went wrong.");
                }
            } else {
                setEvent({});
                setEventFighters([]);
            }
        })();
    }, [eventId, navigate]);

    useEffect(() => {
        const handleKeyDown = (event) => {
            if (event.key === "Escape") {
                const activeElement = document.activeElement;
                if (
                    activeElement &&
                    ["input", "textarea", "select"].includes(
                        activeElement.tagName.toLowerCase()
                    )
                ) {
                    activeElement.blur();
                }
            }

            if ((event.metaKey || event.ctrlKey) && event.key === "Enter") {
                if (changed) {
                    event.preventDefault();
                    handleSubmit();
                }
            }
        };

        window.addEventListener("keydown", handleKeyDown);

        return () => {
            window.removeEventListener("keydown", handleKeyDown);
        };
    }, [handleSubmit, changed]);

    // Load available fighters for add fighter modal
    useEffect(() => {
        const loadAvailableFighters = async () => {
            if (!eventId || !eventFighters) return;
            try {
                const fightersResult = await getFighters({
                    size: 1000,
                    order: "name",
                    sort: "asc",
                });
                if (fightersResult && fightersResult.data) {
                    // Filter out fighters already in event
                    const registeredFighterIds = new Set(
                        eventFighters.map((ef) => ef.fighterId)
                    );
                    const available = fightersResult.data.filter(
                        (fighter) => !registeredFighterIds.has(fighter.id)
                    );
                    setAvailableFighters(available);
                }
            } catch (error) {
                console.error("Error loading available fighters:", error);
            }
        };
        loadAvailableFighters();
    }, [eventId, eventFighters]);

    const handleAddFighterToEvent = async () => {
        if (!selectedFighterForAdd || !selectedEntryForAdd) {
            toast.error("Please select a fighter and entry type");
            return;
        }

        if (event?.isEnded) {
            toast.error("Cannot add fighters to ended event");
            return;
        }

        const dialog = document.getElementById("add-fighter-modal");
        dialog.close();

        await toast.promise(
            addFighterToEvent({
                eventId: eventId,
                fighterId: selectedFighterForAdd.id,
                entry: selectedEntryForAdd,
                eventWeightClass: selectedEventWeightClassForAdd || null,
            }),
            {
                loading: "Adding fighter to event...",
                success: () => {
                    // Reset form
                    setSelectedFighterForAdd(null);
                    setSelectedEntryForAdd(null);
                    setSelectedEventWeightClassForAdd(null);
                    // Refresh event fighters list by reloading event data
                    window.location.reload();
                    return "Fighter added successfully";
                },
                error: "Failed to add fighter",
            }
        );
    };

    const handleRemoveFighter = (eventFighter) => {
        if (event?.isEnded) {
            toast.error("Cannot remove fighters from ended event");
            return;
        }
        setFighterToRemove(eventFighter);
        const dialog = document.getElementById("remove-fighter-dialog");
        dialog.showModal();
    };

    const handleConfirmRemoveFighter = async () => {
        if (!fighterToRemove) return;

        const dialog = document.getElementById("remove-fighter-dialog");
        dialog.close();

        await toast.promise(
            removeFighterFromEvent({
                eventId: eventId,
                fighterId: fighterToRemove.fighterId,
            }),
            {
                loading: "Removing fighter from event...",
                success: () => {
                    setFighterToRemove(null);
                    // Refresh event fighters list by reloading event data
                    window.location.reload();
                    return "Fighter removed successfully";
                },
                error: "Failed to remove fighter",
            }
        );
    };

    const handlePullOutFighter = async (eventFighter) => {
        if (!eventFighter) return;

        const currentIsPulledOut = eventFighter.isPulledOut ?? false;

        if (!currentIsPulledOut && !!eventFighter.matchWith) {
            setPullOutBlockedFighter(eventFighter);
            const dialog = document.getElementById("pull-out-blocked-dialog");
            dialog?.showModal();
            return;
        }

        try {
            await toast.promise(
                pullOutFighter(
                    eventId,
                    eventFighter.fighterId,
                    currentIsPulledOut
                ),
                {
                    loading: currentIsPulledOut
                        ? "Restoring fighter..."
                        : "Pulling out fighter...",
                    success: () => {
                        setEventFighters((prev) => {
                            if (!prev) return prev;
                            return prev.map((ef) =>
                                ef.fighterId === eventFighter.fighterId
                                    ? { ...ef, isPulledOut: !currentIsPulledOut }
                                    : ef
                            );
                        });
                        return currentIsPulledOut
                            ? "Fighter restored"
                            : "Fighter pulled out";
                    },
                    error: "Failed to update pull-out status",
                }
            );
        } catch (error) {
            console.error("Error toggling pull-out:", error);
            window.location.reload();
        }
    };

    const refreshTokenCounts = useCallback(async (fighters) => {
        const ids = (fighters ?? [])
            .map((ef) => ef?.fighterId)
            .filter(Boolean);
        if (ids.length === 0) {
            setTokenCountsByFighterId({});
            return;
        }
        const counts = await fetchActiveTokenCountsForFighters(ids);
        setTokenCountsByFighterId(counts);
    }, []);

    useEffect(() => {
        if (!eventFighters) return;
        refreshTokenCounts(eventFighters);
    }, [eventFighters, refreshTokenCounts]);

    // --- Weight for THIS event only ---
    // Scoped deliberately: it writes one `event-fighters` doc and never touches
    // the fighter's profile. Fixing a weight for one card shouldn't rewrite the
    // fighter's record everywhere — that's what the manage-fighters prompt is
    // for, and it asks before it reaches across events.
    const handleEditWeight = (eventFighter) => {
        if (!eventFighter) return;
        if (event?.isEnded) {
            toast.error("Cannot change weight on an ended event");
            return;
        }
        setWeightEditFighter(eventFighter);
        setWeightEditClass(getEventFighterWeightClass(eventFighter) || null);
    };

    const handleConfirmWeightEdit = async () => {
        if (!weightEditFighter || savingWeight) return;
        const weightClass = weightEditClass;
        if (!weightClass) {
            return toast.error("Pick a weight class first.");
        }
        setSavingWeight(true);
        try {
            await applyWeightToRegistrations(
                weightEditFighter.fighterId,
                [weightEditFighter.id],
                weightClass
            );
            // Mirror the write locally so the roster updates without a refetch.
            const flagged =
                !!weightEditFighter.matchWith &&
                weightEditFighter.eventWeightClass !== weightClass;
            setEventFighters((prev) =>
                (prev || []).map((ef) =>
                    ef.id === weightEditFighter.id
                        ? {
                              ...ef,
                              eventWeightClass: weightClass,
                              needsRematchReview:
                                  flagged || ef.needsRematchReview === true,
                          }
                        : ef
                )
            );
            setWeightEditFighter(null);
            toast.success(
                flagged
                    ? `${weightClass} ${weightClassesName[weightClass]} — bout flagged for rematch review.`
                    : `${weightClass} ${weightClassesName[weightClass]}`
            );
        } catch (error) {
            console.error("Error on handleConfirmWeightEdit(): ", error);
            toast.error("Something went wrong when saving the weight.");
        } finally {
            setSavingWeight(false);
        }
    };

    // --- Refund (issue credit + record on the event) ---
    const handleRefund = (eventFighter) => {
        if (!eventFighter) return;
        if (event?.isEnded) {
            toast.error("Cannot refund on an ended event");
            return;
        }
        setFighterToRefund(eventFighter);
        const dialog = document.getElementById("refund-dialog");
        dialog?.showModal();
    };

    const handleConfirmRefund = async () => {
        if (!fighterToRefund || refunding) return;
        setRefunding(true);
        try {
            await toast.promise(
                refundEventFighter({
                    eventId: eventId,
                    fighterId: fighterToRefund.fighterId,
                }),
                {
                    loading: "Refunding fighter...",
                    success: (result) => {
                        if (!result || result.success === false) {
                            throw new Error(
                                result?.error || "Failed to refund fighter"
                            );
                        }
                        return "Fighter refunded — 1 credit issued";
                    },
                    error: (err) => err?.message || "Failed to refund fighter",
                }
            );
            const dialog = document.getElementById("refund-dialog");
            dialog?.close();
            setFighterToRefund(null);
            window.location.reload();
        } finally {
            setRefunding(false);
        }
    };

    // --- Rejoin a refunded fighter by spending one of their credits ---
    const handleRejoinWithToken = (eventFighter) => {
        if (!eventFighter) return;
        if (event?.isEnded) {
            toast.error("Cannot rejoin on an ended event");
            return;
        }
        setFighterToRejoin(eventFighter);
        const dialog = document.getElementById("rejoin-dialog");
        dialog?.showModal();
    };

    const handleConfirmRejoin = async () => {
        if (!fighterToRejoin || rejoining) return;
        setRejoining(true);
        try {
            await toast.promise(
                rejoinEventFighterWithToken({
                    eventId: eventId,
                    fighterId: fighterToRejoin.fighterId,
                }),
                {
                    loading: "Rejoining fighter...",
                    success: (result) => {
                        if (!result || result.success === false) {
                            throw new Error(
                                result?.error || "Failed to rejoin fighter"
                            );
                        }
                        return "Fighter rejoined — 1 credit spent";
                    },
                    error: (err) => err?.message || "Failed to rejoin fighter",
                }
            );
            const dialog = document.getElementById("rejoin-dialog");
            dialog?.close();
            setFighterToRejoin(null);
            window.location.reload();
        } finally {
            setRejoining(false);
        }
    };

    useEffect(() => {
        setMatchedFighters(getUniqueMatchedFighters(eventFighters));
    }, [eventFighters]);

    /* ---------------- filtered + sorted views ----------------
       All pure, in-memory derivations of the roster that is already fetched
       above. The Results view (ended event) and the Matched tab work in
       BOUTS: a pair survives when either corner matches, and each entry
       carries its original bout index so "bout 7" stays bout 7. */

    const isResultsView = !!event?.isEnded && activeTab === "matched";
    const isPairTab = activeTab === "matched";

    const filterOpts = useMemo(
        () => ({
            eventDate: event?.dateTime,
            // Feeds the "Weight change" flag chip — a pending request is a
            // property of the fighter, not a row on the registration.
            pendingWeightChangeIds,
        }),
        [event?.dateTime, pendingWeightChangeIds]
    );

    // The unfiltered rows of the active tab — what the chips count against.
    const rosterPool = useMemo(() => {
        const list = eventFighters || [];
        if (activeTab === "pulledOut") {
            return list.filter((ef) => !!ef.isPulledOut);
        }
        if (activeTab === "refunded") {
            return list.filter(
                (ef) =>
                    ef.isRefunded ||
                    (Array.isArray(ef.refundHistory) &&
                        ef.refundHistory.length > 0)
            );
        }
        return list.filter((ef) => !ef.isPulledOut);
    }, [eventFighters, activeTab]);

    const visibleRoster = useMemo(() => {
        if (isPairTab) return [];
        const filtered = rosterPool.filter((ef) =>
            matchesFilters(ef, filters, filterOpts)
        );
        return sortEventFighters(filtered, effectiveSort, sortOrder, {
            ...filterOpts,
            tokenCounts: tokenCountsByFighterId,
        });
    }, [
        isPairTab,
        rosterPool,
        filters,
        filterOpts,
        effectiveSort,
        sortOrder,
        tokenCountsByFighterId,
    ]);

    // Bouts, in their original order. `pairKey` is the identity the
    // win/draw/win-by handlers key off — never reindex or mutate it.
    const pairEntries = useMemo(() => {
        if (isResultsView) {
            return (event?.eventResults || [])
                .map((result, index) => ({
                    result,
                    pair: {
                        fighterA: (eventFighters || []).find(
                            (ef) => ef.fighterId === result.fighterAId
                        ),
                        fighterB: (eventFighters || []).find(
                            (ef) => ef.fighterId === result.fighterBId
                        ),
                    },
                    originalIndex: index,
                }))
                .filter((entry) => entry.pair.fighterA && entry.pair.fighterB);
        }
        // Bout numbers come from the admin's saved running order. Pairs matched
        // since the last reorder land at the end rather than vanishing.
        return applyBoutOrder(
            Object.entries(matchedFighters).map(([pairKey, pair]) => ({
                pairKey,
                pair,
            })),
            event?.boutOrder
        );
    }, [
        isResultsView,
        event?.eventResults,
        event?.boutOrder,
        eventFighters,
        matchedFighters,
    ]);

    /** Every bout in running order — what a reorder is computed against. */
    const orderedPairKeys = useMemo(
        () => pairEntries.map((entry) => entry.pairKey).filter(Boolean),
        [pairEntries]
    );

    // END EVENT stays disabled while any bout it would send (pulled-out ones
    // are cancelled, not sent) lacks a WIN / DRAW pick, or has a winner but no
    // method: updateRecords would write `winBy: undefined` and fail.
    const endEventCheck = useMemo(
        () => endEventReadiness(matchedFighters, orderedPairKeys),
        [matchedFighters, orderedPairKeys]
    );
    const endEventBlocked =
        !event?.isEnded && !event?.isTest && !endEventCheck.ready;
    const endEventBlockedWhy = endEventBlockedText(endEventCheck);

    /* ---------------- fight card export ----------------
       The whole card in running order as an Excel sheet laid out like the
       printed card (see utils/fight-card-export), whatever the filters.
       Admins finish it off in Excel: time slots, last-minute changes. */

    const [fightCardBusy, setFightCardBusy] = useState(false);

    const handleExportFightCard = async () => {
        const card = buildFightCard(
            pairEntries.map((entry) => ({
                ...entry,
                // Results-view entries carry fighter ids, not a pairKey.
                pairKey:
                    entry.pairKey ??
                    pairKeyOf(
                        entry.pair?.fighterA?.fighterId,
                        entry.pair?.fighterB?.fighterId
                    ),
            })),
            event
        );
        if (card.sections.length === 0) {
            toast.error("There are no matches to export yet.");
            return;
        }
        setFightCardBusy(true);
        try {
            const excel = await import("exceljs");
            const workbook = writeFightCardWorkbook(
                excel.default ?? excel,
                card
            );
            const buffer = await workbook.xlsx.writeBuffer();
            const url = URL.createObjectURL(
                new Blob([buffer], {
                    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
                })
            );
            const a = document.createElement("a");
            a.href = url;
            a.download = fightCardFileName(event?.name);
            a.click();
            setTimeout(() => URL.revokeObjectURL(url), 1000);
            toast.success(
                card.skipped > 0
                    ? `Fight card downloaded. ${card.skipped} cancelled ${card.skipped === 1 ? "match was" : "matches were"} left out.`
                    : "Fight card downloaded."
            );
        } catch (error) {
            console.error("Error on handleExportFightCard(): ", error);
            toast.error("Couldn't build the fight card. Try again.");
        } finally {
            setFightCardBusy(false);
        }
    };

    // Reordering only makes sense against the running order. Under any other
    // sort the list isn't the card, so dragging would write an order the admin
    // can't see.
    const canReorder =
        isPairTab &&
        !isResultsView &&
        !event?.isEnded &&
        effectiveSort === "bout" &&
        sortOrder === "asc";

    // This page reads the event once, while the results dashboard can move
    // bouts during the event, so a write only goes if the stored order is
    // still the one this tab last saw (setEventBoutOrder). Writes go one at
    // a time, each expecting what the one before stored, so quick nudges
    // don't refuse each other; once one fails, the ones queued behind it
    // were built on an order that never saved and are dropped.
    const persistBoutOrder = useCallback(
        (nextOrder) => {
            setEvent((prev) => ({ ...prev, boutOrder: nextOrder }));
            if (!eventId) return;
            const epoch = boutOrderEpoch.current;
            boutOrderWrites.current = boutOrderWrites.current.then(async () => {
                if (epoch !== boutOrderEpoch.current) return;
                try {
                    await setEventBoutOrder(
                        eventId,
                        nextOrder,
                        storedBoutOrder.current
                    );
                    storedBoutOrder.current = nextOrder;
                } catch (error) {
                    console.error("Failed to save bout order:", error);
                    boutOrderEpoch.current += 1;
                    if (error?.code === "STALE_ORDER") {
                        storedBoutOrder.current = error.boutOrder;
                        toast.error(
                            "The running order was changed on another screen (the live results page?), so this move wasn't saved. This page now shows the current order: check it, then move the bout again.",
                            { duration: 10000 }
                        );
                    } else {
                        toast.error(
                            error?.code === "EVENT_ENDED"
                                ? error.message
                                : "Couldn't save the running order."
                        );
                    }
                    setEvent((prev) => ({
                        ...prev,
                        boutOrder: storedBoutOrder.current,
                    }));
                }
            });
        },
        [eventId]
    );

    /** Drop `movingKey` in front of `beforeKey` (null drops it last). */
    const handleReorderBout = useCallback(
        (movingKey, beforeKey) => {
            if (!canReorder || !movingKey) return;
            const next = moveBout(orderedPairKeys, movingKey, beforeKey);
            if (next.join("|") === orderedPairKeys.join("|")) return;
            persistBoutOrder(next);
        },
        [canReorder, orderedPairKeys, persistBoutOrder]
    );

    /** Nudge a bout one slot — the touch and keyboard path to the same result. */
    const handleNudgeBout = useCallback(
        (pairKey, delta) => {
            if (!canReorder) return;
            const from = orderedPairKeys.indexOf(pairKey);
            const to = from + delta;
            if (from < 0 || to < 0 || to >= orderedPairKeys.length) return;
            // Moving down means landing AFTER the neighbour, which is "before
            // the one past it" in moveBout's terms.
            const beforeKey =
                delta < 0
                    ? orderedPairKeys[to]
                    : orderedPairKeys[to + 1] ?? null;
            persistBoutOrder(moveBout(orderedPairKeys, pairKey, beforeKey));
        },
        [canReorder, orderedPairKeys, persistBoutOrder]
    );

    const visiblePairs = useMemo(() => {
        if (!isPairTab) return [];
        const filtered = pairEntries.filter((entry) =>
            matchesFiltersPair(entry.pair, filters, filterOpts)
        );
        return sortPairEntries(
            filtered,
            effectiveSort,
            sortOrder,
            filterOpts
        );
    }, [isPairTab, pairEntries, filters, filterOpts, effectiveSort, sortOrder]);

    const visiblePairFighters = useMemo(
        () => fightersInPairs(visiblePairs),
        [visiblePairs]
    );
    // "Export full roster" deliberately has no memo of its own: it calls
    // handleExportToCSV() with no list, which is the pre-filter behaviour —
    // every event fighter, pulled-out included, sorted by entry.

    const filterPool = isPairTab ? pairEntries : rosterPool;
    const shownCount = isPairTab ? visiblePairs.length : visibleRoster.length;
    const totalCount = isPairTab ? pairEntries.length : rosterPool.length;

    // Distinct from the genuine "nothing here" states — this one says the
    // filters are hiding rows, and offers the way out.
    const noMatchesBlock = (noun, total) => (
        <div className="w-full h-full flex-center">
            <div className="flex flex-col items-center gap-3 text-center">
                <p className="font-semibold">
                    No {noun} match these filters.
                </p>
                <p className="text-sm opacity-70">
                    {total} {noun} on this tab — try widening the chips.
                </p>
                <button
                    className="btn btn-sm btn-primary gap-2"
                    onClick={handleClearFilters}
                >
                    <X size={16} />
                    Clear filters
                </button>
            </div>
        </div>
    );

    // Empty roster message. Shown on its own for an unsaved event, and under
    // the tab strip for a saved one.
    const emptyRosterBlock = (
        <div className="w-full h-full flex-center">
            <div className="flex flex-col items-center gap-4">
                <p>
                    No fighter register for this event
                    yet.
                </p>
                {!event?.isEnded &&
                    (eventId ? (
                        <button
                            className="btn btn-primary"
                            onClick={() => {
                                const dialog =
                                    document.getElementById(
                                        "add-fighter-modal"
                                    );
                                dialog.showModal();
                            }}
                        >
                            <Plus size={20} />
                            Add Fighter Manually
                        </button>
                    ) : (
                        <p className="text-sm opacity-70">
                            Save the event first to add
                            fighters manually.
                        </p>
                    ))}
            </div>
        </div>
    );

    return (
        <>
            {event === null ? (
                <div className="page-size flex-center">
                    <div className="loading loading-lg loading-bars" />
                </div>
            ) : (
                <>
                    <button
                        className="fixed md:top-2 right-2 btn btn-square"
                        onClick={handleClose}
                    >
                        <X />
                    </button>
                    <div className="flex flex-col md:flex-row gap-4">
                        <div className="w-full flex-center min-h-screen h-auto md:h-screen overflow-y-auto justify-start p-4 py-6 gap-4">
                            <div className="w-full flex flex-row items-center justify-between">
                                <p className="text-xl font-bold">
                                    {!!eventId ? "Manage" : "Create"} Event:{" "}
                                </p>
                                {event?.isEnded && (
                                    <div className="badge badge-error badge-lg">
                                        <span className="font-bold">
                                            EVENT ENDED
                                        </span>
                                    </div>
                                )}
                            </div>
                            <EventReadinessPanel
                                event={{
                                    ...event,
                                    coverImage:
                                        event?.coverImage ||
                                        (newCoverImage ? "__pending__" : null),
                                }}
                            />
                            {/* Image */}
                            <div className="flex flex-row gap-4 justify-stretch items-stretch w-full">
                                <div className="w-full min-h-36 border flex-row">
                                    {!!newCoverImage ? (
                                        <div className="relative flex-center">
                                            <img
                                                className="w-full h-full object-cover object-center"
                                                src={URL.createObjectURL(
                                                    newCoverImage
                                                )}
                                                alt={`${event.name} New Picture`}
                                            />
                                            <button
                                                className="btn btn-circle btn-sm btn-outline absolute bottom-0 right-0 translate-x-1/3 translate-y-1/3 bg-base-100"
                                                onClick={
                                                    handleUploadEventCoverImage
                                                }
                                            >
                                                <UploadCloudIcon />
                                            </button>
                                        </div>
                                    ) : !!event.coverImage ? (
                                        <img
                                            className="w-full h-full object-cover object-center"
                                            src={event?.coverImage ?? ""}
                                            alt={`Cover Image for ${event?.name ?? ""
                                                }`}
                                        />
                                    ) : (
                                        <div className="w-full aspect-video flex-center">
                                            <ImageOff size={36} />
                                        </div>
                                    )}
                                </div>
                                <div
                                    className={
                                        "border-4 border-dotted flex-center flex-center hover:bg-base-200 cursor-pointer w-86 p-4 " +
                                        (coverImageInputDragActive
                                            ? " !bg-base-300 "
                                            : "")
                                    }
                                    onClick={() =>
                                        coverImageInputRef?.current?.click()
                                    }
                                    onDragEnter={handleDrag}
                                    onDragOver={handleDrag}
                                    onDragLeave={handleDrag}
                                    onDrop={handleDrop}
                                >
                                    <p>
                                        {!coverImageInputDragActive
                                            ? "Drop / Select a new Image."
                                            : "Drop it!"}
                                    </p>
                                    <span className="text-sm">
                                        {"[PNG, JPEG, JPG]"}
                                    </span>
                                    <input
                                        onChange={handleChange}
                                        type="file"
                                        accept="image/png, image/jpeg, image/jpg"
                                        name="coverImage"
                                        id="coverImage"
                                        ref={coverImageInputRef}
                                        className="hidden"
                                    />
                                </div>
                            </div>
                            {/* Inputs */}
                            <div className="flex flex-col w-full flex-1 gap-2">
                                <label htmlFor="ticketUrl">Ticket URL:</label>
                                {ticketUrlInput}
                                <br />

                                <div className="flex w-full flex-row gap-2 items-center justify-between">
                                    <label htmlFor="liveStreamUrl-0">
                                        Live Stream URLs:
                                    </label>
                                    <Link
                                        to={`${LIVE_STREAM_PATH}/${event?.id}`}
                                        target="_blank"
                                        rel="noreferrer"
                                    >
                                        <button className="btn btn-outline btn-sm">
                                            Open in new tab
                                        </button>
                                    </Link>
                                </div>
                                {liveStreamUrlInput}
                                <br />
                                <label htmlFor="liveStreamTicketPrice">
                                    Live Stream Ticket Price:
                                </label>
                                {liveStreamTicketPriceInput}
                                <br />
                                <label htmlFor="name">Name:</label>
                                {nameInput}
                                <br />
                                <label htmlFor="details">Details:</label>
                                {detailsInput}
                                <p>Details [Preview]: </p>
                                <div className="w-full p-4 prose textarea textarea-bordered max-w-none">
                                    <Markdown>{event?.details}</Markdown>
                                </div>
                                <br />
                                <label htmlFor="status">Status:</label>
                                {statusInput}
                                {testModeInput}
                                {holdScoresInput}
                                {event?.isEnded && (
                                    <div className="alert alert-info">
                                        <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            fill="none"
                                            viewBox="0 0 24 24"
                                            className="stroke-current shrink-0 w-6 h-6"
                                        >
                                            <path
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                strokeWidth="2"
                                                d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                                            ></path>
                                        </svg>
                                        <div>
                                            <h3 className="font-bold">
                                                Event Ended
                                            </h3>
                                            <div className="text-xs">
                                                {event?.endedAt &&
                                                    `Ended on: ${formatFirestoreTimestamp(
                                                        event.endedAt
                                                    )}`}
                                            </div>
                                        </div>
                                    </div>
                                )}
                                <br />
                                <label>Event:</label>
                                <div className="flex flex-col w-full gap-2 border p-4 relative">
                                    <label htmlFor="dateTime">Date:</label>
                                    {dateInput}
                                    <label htmlFor="dateTime">Time:</label>
                                    {timeInput}
                                </div>
                                <br />
                                <label>Fighters Register Deadline:</label>
                                <div className="flex flex-col w-full gap-2 border p-4 relative">
                                    <label htmlFor="registerExpiredDateTime">
                                        Date:
                                    </label>
                                    {registerExpiredDateInput}
                                    <label htmlFor="registerExpiredDateTime">
                                        Time:
                                    </label>
                                    {registerExpiredTimeInput}
                                </div>
                                <br />
                                <label>
                                    Live Stream Ticket Purchase Deadline:
                                </label>
                                <div className="flex flex-col w-full gap-2 border p-4 relative">
                                    <label htmlFor="liveStreamTicketPurchaseDeadline">
                                        Date:
                                    </label>
                                    {liveStreamTicketPurchaseDeadlineDateInput}
                                    <label htmlFor="liveStreamTicketPurchaseDeadline">
                                        Time:
                                    </label>
                                    {liveStreamTicketPurchaseDeadlineTimeInput}
                                </div>
                                <br />
                                <label htmlFor="venue">Venue:</label>
                                {venueInput}
                                <br />
                                <label htmlFor="color">Color:</label>
                                {colorInput}
                                <br />
                                <label htmlFor="allowedEntries">
                                    Allowed Entries:
                                </label>
                                <div className="flex flex-col gap-2 w-full">
                                    {Object.keys(
                                        fighterEntryDisplayMapping
                                    ).map((key) => (
                                        <label
                                            key={key}
                                            className="flex items-center gap-2 cursor-pointer"
                                        >
                                            <input
                                                type="checkbox"
                                                className="checkbox checkbox-sm"
                                                checked={
                                                    Array.isArray(
                                                        event?.allowedEntries
                                                    ) &&
                                                    event.allowedEntries.includes(
                                                        key
                                                    )
                                                }
                                                onChange={(e) => {
                                                    if (!changed)
                                                        setChanged(true);
                                                    setEvent((prev) => {
                                                        const current =
                                                            Array.isArray(
                                                                prev?.allowedEntries
                                                            )
                                                                ? prev.allowedEntries
                                                                : [];
                                                        if (e.target.checked) {
                                                            return {
                                                                ...prev,
                                                                allowedEntries:
                                                                    [
                                                                        ...current,
                                                                        key,
                                                                    ],
                                                            };
                                                        } else {
                                                            return {
                                                                ...prev,
                                                                allowedEntries:
                                                                    current.filter(
                                                                        (
                                                                            item
                                                                        ) =>
                                                                            item !==
                                                                            key
                                                                    ),
                                                            };
                                                        }
                                                    });
                                                }}
                                            />
                                            <span>
                                                {
                                                    fighterEntryDisplayMapping[
                                                    key
                                                    ]
                                                }
                                            </span>
                                        </label>
                                    ))}
                                </div>
                                <p className="text-xs opacity-70">
                                    Leave empty to allow all entries.
                                </p>
                                <br />
                                <label htmlFor="createdAt">CreatedAt:</label>
                                {createdAtInput}
                                <br />
                                <label htmlFor="updatedAt">UpdatedAt:</label>
                                {updatedAtInput}
                                <br />
                                {/* Submit Button */}
                                <button
                                    className={
                                        "btn btn-primary w-full disabled:!bg-gray-400 flex flex-row gap-4 "
                                    }
                                    disabled={!changed}
                                    onClick={handleSubmit}
                                >
                                    <p>{!!eventId ? "Save" : "Create"}</p>
                                    {changed ? (
                                        <span>
                                            <kbd className="kbd kbd-xs">
                                                {isMac() ? "Cmd" : "Ctrl"}
                                            </kbd>{" "}
                                            +{" "}
                                            <kbd className="kbd kbd-xs">
                                                Enter
                                            </kbd>
                                        </span>
                                    ) : null}
                                </button>
                            </div>
                        </div>
                        <div className="w-full flex-center min-h-screen h-auto md:h-screen overflow-y-auto md:overflow-hidden justify-start items-start p-4 py-6 gap-4">
                            <div className="w-full flex flex-row flex-wrap items-center gap-x-4 gap-y-2">
                                <p className="text-lg font-bold">
                                    Event Fighters
                                    {!!eventFighters
                                        ? ` (${eventFighters.length})`
                                        : ""}
                                    :
                                </p>
                                {/* The live results dashboard, in its own tab so
                                    this page (and any unsaved result picks) stays put. */}
                                {!!eventId && (
                                    <a
                                        href={`${EVENT_RESULTS_PATH}/${eventId}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="btn btn-sm btn-outline gap-2"
                                        title="Open the live results dashboard in a new tab"
                                    >
                                        <MonitorPlay size={16} />
                                        Results dashboard
                                    </a>
                                )}
                            </div>
                            <div className="divider m-0" />
                            {!eventFighters ? (
                                <div className="w-full h-full flex-center">
                                    <div className="loading loading-lg loading-bars" />
                                </div>
                            ) : eventFighters.length < 1 && !eventId ? (
                                emptyRosterBlock
                            ) : (
                                // Rendered whenever the event is saved, even with
                                // an empty roster, so the Judges tab is reachable
                                // before anyone registers.
                                // md+: header, tabs and filters stay put; only
                                // the list below them scrolls (flex-1 min-h-0).
                                <div className="flex flex-col w-full gap-2 h-full md:h-auto md:flex-1 md:min-h-0">
                                    <div
                                        role="tablist"
                                        // DaisyUI's .tabs is `display:grid`, which by default sizes
                                        // columns to the container and wraps. Force one row of
                                        // content-width columns and scroll it sideways instead, so
                                        // four tabs never outgrow a narrow panel.
                                        className="tabs tabs-lifted grid-flow-col auto-cols-max justify-start overflow-x-auto overflow-y-hidden [&::-webkit-scrollbar]:hidden [scrollbar-width:none]"
                                    >
                                        {tabs.map((t, index) => {
                                            let suffix = "";
                                            if (t.value === "matched") {
                                                const pairs = (eventFighters.filter(ef => ef.matchWith).length ?? 0) / 2;
                                                if (pairs > 0) suffix = `(${pairs} Pairs)`;
                                            } else if (t.value === "all") {
                                                const n = eventFighters.filter(ef => !ef.isPulledOut).length;
                                                suffix = `(${n})`;
                                            } else if (t.value === "pulledOut") {
                                                const n = eventFighters.filter(ef => !!ef.isPulledOut).length;
                                                suffix = `(${n})`;
                                            } else if (t.value === "refunded") {
                                                const n = eventFighters.filter(ef => ef.isRefunded || (Array.isArray(ef.refundHistory) && ef.refundHistory.length > 0)).length;
                                                suffix = `(${n})`;
                                            } else if (t.value === "judges") {
                                                if (judgeCount !== null) suffix = `(${judgeCount})`;
                                            }
                                            return (
                                                <button
                                                    key={index}
                                                    role="tab"
                                                    // .tab is itself `inline-flex; flex-wrap:wrap` at a
                                                    // fixed 2rem height, so "Matched" and "(22 Pairs)"
                                                    // wrapped onto a second line that spilled out of
                                                    // the tab. flex-nowrap is the fix; --tab-padding is
                                                    // DaisyUI's own knob for tightening the gutters.
                                                    className={twMerge(
                                                        "tab flex-nowrap whitespace-nowrap text-xs [--tab-padding:0.55rem] sm:text-sm sm:[--tab-padding:0.9rem]",
                                                        activeTab === t.value
                                                            ? "tab-active"
                                                            : ""
                                                    )}
                                                    onClick={() =>
                                                        setActiveTab(t.value)
                                                    }
                                                >
                                                    {t.label}
                                                    {suffix ? (
                                                        <span className="ml-1 opacity-70">
                                                            {suffix}
                                                        </span>
                                                    ) : null}
                                                </button>
                                            )
                                        })}
                                    </div>
                                    {activeTab !== "judges" &&
                                        eventFighters.length > 0 && (
                                            <EventFighterFilterBar
                                                filters={filters}
                                                onChange={setFilters}
                                                sort={effectiveSort}
                                                order={sortOrder}
                                                onSortChange={handleSortChange}
                                                pool={filterPool}
                                                eventDate={event?.dateTime}
                                                tab={activeTab}
                                                shownCount={shownCount}
                                                totalCount={totalCount}
                                            />
                                        )}
                                    {activeTab === "judges" ? (
                                        <div className="w-full h-[calc(100vh-10rem)] md:h-auto md:flex-1 md:min-h-0 md:overflow-y-auto">
                                            <EventJudgesPanel
                                                eventId={eventId}
                                                eventName={event?.name}
                                                isEnded={!!event?.isEnded}
                                                judges={eventJudges}
                                                judgesError={eventJudgesError}
                                                scorecards={scorecards}
                                                boutKeys={orderedPairKeys}
                                            />
                                        </div>
                                    ) : eventFighters.length < 1 ? (
                                        <div className="w-full h-[calc(100vh-10rem)] md:h-auto md:flex-1 md:min-h-0 md:overflow-y-auto">
                                            {emptyRosterBlock}
                                        </div>
                                    ) : event?.isEnded &&
                                        activeTab !== "all" &&
                                        activeTab !== "pulledOut" &&
                                        activeTab !== "refunded" &&
                                        activeTab !== "judges" ? (
                                        // Results view = the "Results" tab on an ended event.
                                        // The roster tabs (all / pulledOut / refunded) still
                                        // render below (read-only) so admins keep the history.
                                        <div className="flex-center w-full gap-2 h-[calc(100vh-10rem)] md:h-auto md:flex-1 md:min-h-0">
                                            <div className="w-full h-full overflow-y-auto flex flex-col gap-2">
                                                {!eventFighters ? (
                                                    // Loading state for event results
                                                    <div className="w-full h-full flex-center">
                                                        <div className="flex flex-col items-center gap-4">
                                                            <div className="loading loading-bars loading-lg"></div>
                                                            <p className="text-lg font-semibold">
                                                                Loading Event
                                                                Results...
                                                            </p>
                                                        </div>
                                                    </div>
                                                ) : pairEntries.length === 0 ? (
                                                    <div className="w-full h-full flex-center">
                                                        <p>
                                                            No results
                                                            available.
                                                        </p>
                                                    </div>
                                                ) : visiblePairs.length ===
                                                    0 ? (
                                                    noMatchesBlock(
                                                        "bouts",
                                                        pairEntries.length
                                                    )
                                                ) : (
                                                    visiblePairs.map(
                                                        (entry) => {
                                                            const result =
                                                                entry.result;
                                                            const index =
                                                                entry.originalIndex;
                                                            // Red (left) / blue (right), same corners as
                                                            // the Matched tab.
                                                            const {
                                                                red: fighterA,
                                                                blue: fighterB,
                                                            } = resolveCorners(
                                                                entry.pair.fighterA,
                                                                entry.pair.fighterB,
                                                                event?.boutCorners?.[
                                                                    pairKeyOf(
                                                                        entry.pair.fighterA?.fighterId,
                                                                        entry.pair.fighterB?.fighterId
                                                                    )
                                                                ]
                                                            );

                                                            const sameEntry =
                                                                fighterA.entry ===
                                                                fighterB.entry;
                                                            const weightClassA =
                                                                getEventFighterWeightClass(
                                                                    fighterA
                                                                );
                                                            const weightClassB =
                                                                getEventFighterWeightClass(
                                                                    fighterB
                                                                );
                                                            const sameWeightClass =
                                                                weightClassA ===
                                                                weightClassB;
                                                            const sameEntrySameWeightClass =
                                                                sameEntry &&
                                                                sameWeightClass;

                                                            return (
                                                                <div
                                                                    key={index}
                                                                    className={twMerge(
                                                                        "flex-center w-full gap-2 p-2",
                                                                        CORNER_GRADIENT
                                                                    )}
                                                                >
                                                                    <div className="flex flex-row gap-2 w-full justify-between">
                                                                        <div className="flex flex-col items-center gap-2 p-2 w-full">
                                                                            <div className="w-24 aspect-square relative">
                                                                                <img
                                                                                    className="w-full h-full object-center object-cover aspect-square"
                                                                                    src={
                                                                                        fighterA
                                                                                            ?.fighter
                                                                                            ?.fighterPicture ||
                                                                                        "/assets/img/example_fighter_picture.png"
                                                                                    }
                                                                                    alt=""
                                                                                />
                                                                                {result.winnerId ===
                                                                                    fighterA.fighterId && (
                                                                                        <div className="absolute -top-2 -right-2 bg-white text-black rounded-full w-8 h-8 flex items-center justify-center text-sm font-bold p-2">
                                                                                            <Crown />
                                                                                        </div>
                                                                                    )}
                                                                            </div>
                                                                            <p className="font-bold text-center">
                                                                                {fighterA
                                                                                    ?.fighter
                                                                                    ?.name ||
                                                                                    "Unknown Fighter"}
                                                                            </p>
                                                                        </div>
                                                                        <div className="flex-center gap-2 w-full">
                                                                            {!sameEntrySameWeightClass && (
                                                                                <>
                                                                                    <p className="self-start text-sm leading-none font-bold">
                                                                                        {fighterA?.entry ||
                                                                                            "Unknown Entry"}
                                                                                    </p>
                                                                                    <p className="self-start text-sm leading-none font-bold">
                                                                                        {(() => {
                                                                                            const wc =
                                                                                                getEventFighterWeightClass(
                                                                                                    fighterA
                                                                                                );
                                                                                            return wc
                                                                                                ? `${wc}: ${weightClassesName[wc]}`
                                                                                                : "Unknown Weight Class";
                                                                                        })()}
                                                                                    </p>
                                                                                </>
                                                                            )}
                                                                            <div className="divider m-0">
                                                                                vs
                                                                            </div>
                                                                            <p
                                                                                className={twMerge(
                                                                                    "text-sm leading-none font-bold",
                                                                                    !sameEntrySameWeightClass &&
                                                                                    "self-end"
                                                                                )}
                                                                            >
                                                                                {fighterB?.entry ||
                                                                                    "Unknown Entry"}
                                                                            </p>
                                                                            <p
                                                                                className={twMerge(
                                                                                    "text-sm leading-none font-bold",
                                                                                    !sameEntrySameWeightClass &&
                                                                                    "self-end"
                                                                                )}
                                                                            >
                                                                                {(() => {
                                                                                    const wc =
                                                                                        getEventFighterWeightClass(
                                                                                            fighterB
                                                                                        );
                                                                                    return wc
                                                                                        ? `${wc}: ${weightClassesName[wc]}`
                                                                                        : "Unknown Weight Class";
                                                                                })()}
                                                                            </p>
                                                                        </div>
                                                                        <div className="flex flex-col items-center gap-2 p-2 w-full">
                                                                            <div className="w-24 aspect-square relative">
                                                                                <img
                                                                                    className="w-full h-full object-center object-cover aspect-square"
                                                                                    src={
                                                                                        fighterB
                                                                                            ?.fighter
                                                                                            ?.fighterPicture ||
                                                                                        "/assets/img/example_fighter_picture.png"
                                                                                    }
                                                                                    alt=""
                                                                                />
                                                                                {result.winnerId ===
                                                                                    fighterB.fighterId && (
                                                                                        <div className="absolute -top-2 -right-2 bg-white text-black rounded-full w-8 h-8 flex items-center justify-center text-sm font-bold p-2">
                                                                                            <Crown />
                                                                                        </div>
                                                                                    )}
                                                                            </div>
                                                                            <p className="font-bold text-center">
                                                                                {fighterB
                                                                                    ?.fighter
                                                                                    ?.name ||
                                                                                    "Unknown Fighter"}
                                                                            </p>
                                                                        </div>
                                                                    </div>
                                                                    <div className="flex flex-col gap-2 w-full">
                                                                        {result.isDraw ? (
                                                                            <div className="flex border min-h-12 items-center">
                                                                                <div className="divider m-0 w-full px-4">
                                                                                    DRAW
                                                                                </div>
                                                                            </div>
                                                                        ) : (
                                                                            <div className="flex border min-h-12 items-center">
                                                                                <p className="px-4">
                                                                                    WINNER:{" "}
                                                                                    {result.winnerId ===
                                                                                        fighterA.fighterId
                                                                                        ? fighterA
                                                                                            ?.fighter
                                                                                            ?.name ||
                                                                                        "Unknown Fighter"
                                                                                        : fighterB
                                                                                            ?.fighter
                                                                                            ?.name ||
                                                                                        "Unknown Fighter"}
                                                                                </p>
                                                                                <div className="flex-1 w-full" />
                                                                                {result.winBy && (
                                                                                    <div className="h-full px-2 bg-white text-black min-w-12 flex-center font-bold">
                                                                                        {result.winBy.toUpperCase()}
                                                                                    </div>
                                                                                )}
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                            );
                                                        }
                                                    )
                                                )}
                                            </div>
                                            <div className="flex flex-row flex-wrap items-center gap-4 w-full">
                                                <button
                                                    className="btn btn-outline btn-square"
                                                    onClick={
                                                        handleCopyMatchedFighters
                                                    }
                                                >
                                                    <Clipboard />
                                                </button>
                                                <button
                                                    className="btn btn-outline"
                                                    onClick={handleExportFightCard}
                                                    disabled={fightCardBusy}
                                                    title="Download the fight card as an Excel sheet to edit and print"
                                                >
                                                    {fightCardBusy ? (
                                                        <span className="loading loading-spinner loading-sm" />
                                                    ) : (
                                                        <FileSpreadsheet size={18} />
                                                    )}
                                                    <span className="hidden sm:inline">
                                                        Export fight card
                                                    </span>
                                                </button>
                                                {/* Unfiltered, this stays exactly what it always was:
                                                    the whole roster sorted by entry. Filters only ever
                                                    add the narrowed export, never silently shrink the
                                                    one admins already rely on. */}
                                                <button
                                                    className="btn btn-primary flex-1"
                                                    onClick={() =>
                                                        filtersActive
                                                            ? handleExportToCSV(
                                                                  visiblePairFighters
                                                              )
                                                            : handleExportToCSV()
                                                    }
                                                >
                                                    {filtersActive
                                                        ? `Export ${visiblePairFighters.length} filtered to CSV`
                                                        : "Export Results to CSV"}
                                                </button>
                                                {filtersActive && (
                                                    <button
                                                        className="btn btn-ghost btn-sm underline underline-offset-2 font-normal"
                                                        onClick={() =>
                                                            handleExportToCSV()
                                                        }
                                                    >
                                                        Export full roster (
                                                        {eventFighters?.length ??
                                                            0}
                                                        )
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                    ) : // Show normal fighter management when event is not ended
                                        activeTab === "all" ? (
                                            <div className="flex flex-col w-full gap-2 h-[calc(100vh-10rem)] md:h-auto md:flex-1 md:min-h-0">
                                                <div className="w-full h-full overflow-y-auto flex flex-col gap-2">
                                                    {visibleRoster.length === 0 &&
                                                        rosterPool.length > 0 &&
                                                        noMatchesBlock(
                                                            "fighters",
                                                            rosterPool.length
                                                        )}
                                                    {visibleRoster
                                                        .map(
                                                            (eventFighter, index) => (
                                                                <EventFighterListItem
                                                                    key={eventFighter.id ?? index}
                                                                    handleMatchingFighter={
                                                                        event?.isEnded
                                                                            ? undefined
                                                                            : handleMatchingFighter
                                                                    }
                                                                    handleRemoveFighter={
                                                                        event?.isEnded
                                                                            ? undefined
                                                                            : handleRemoveFighter
                                                                    }
                                                                    handlePullOutFighter={
                                                                        event?.isEnded
                                                                            ? undefined
                                                                            : handlePullOutFighter
                                                                    }
                                                                    handleRefund={
                                                                        event?.isEnded ||
                                                                        event?.isTest
                                                                            ? undefined
                                                                            : handleRefund
                                                                    }
                                                                    handleEditWeight={
                                                                        event?.isEnded
                                                                            ? undefined
                                                                            : handleEditWeight
                                                                    }
                                                                    activeTokenCount={
                                                                        tokenCountsByFighterId[
                                                                        eventFighter
                                                                            .fighterId
                                                                        ] ?? 0
                                                                    }
                                                                    eventDate={
                                                                        event?.dateTime
                                                                    }
                                                                    eventFighter={
                                                                        eventFighter
                                                                    }
                                                                />
                                                            )
                                                        )}
                                                </div>
                                                <div className="flex flex-row flex-wrap items-center gap-2 w-full">
                                                    {/* Unfiltered, this stays exactly what it always was:
                                                        the whole roster sorted by entry, pulled-out
                                                        fighters included (the CSV has a "Pulled Out"
                                                        column). Filters only ever add the narrowed
                                                        export alongside it. */}
                                                    <button
                                                        className="btn btn-primary flex-1"
                                                        onClick={() =>
                                                            filtersActive
                                                                ? handleExportToCSV(
                                                                      visibleRoster
                                                                  )
                                                                : handleExportToCSV()
                                                        }
                                                    >
                                                        {filtersActive
                                                            ? `Export ${visibleRoster.length} filtered to CSV`
                                                            : "Export to CSV"}
                                                    </button>
                                                    {/* Adding a fighter is a primary action — it stays
                                                        put whether or not filters are on. */}
                                                    {!event?.isEnded && (
                                                        <button
                                                            className="btn btn-primary flex-1"
                                                            onClick={() => {
                                                                const dialog =
                                                                    document.getElementById(
                                                                        "add-fighter-modal"
                                                                    );
                                                                dialog.showModal();
                                                            }}
                                                        >
                                                            <Plus size={20} />
                                                            Add Fighter Manually
                                                        </button>
                                                    )}
                                                    {filtersActive && (
                                                        <button
                                                            className="btn btn-ghost btn-sm underline underline-offset-2 font-normal"
                                                            onClick={() =>
                                                                handleExportToCSV()
                                                            }
                                                        >
                                                            Export full roster (
                                                            {eventFighters?.length ??
                                                                0}
                                                            )
                                                        </button>
                                                    )}
                                                </div>
                                            </div>
                                        ) : activeTab === "pulledOut" ? (
                                            <div className="flex flex-col w-full gap-2 h-[calc(100vh-10rem)] md:h-auto md:flex-1 md:min-h-0">
                                                <div className="w-full h-full overflow-y-auto flex flex-col gap-2">
                                                    {(() => {
                                                        if (rosterPool.length === 0) {
                                                            return (
                                                                <div className="w-full h-full flex-center">
                                                                    <p>No pulled-out fighters.</p>
                                                                </div>
                                                            );
                                                        }
                                                        if (visibleRoster.length === 0) {
                                                            return noMatchesBlock(
                                                                "fighters",
                                                                rosterPool.length
                                                            );
                                                        }
                                                        return visibleRoster.map(
                                                            (eventFighter, index) => (
                                                                <EventFighterListItem
                                                                    key={eventFighter.id ?? index}
                                                                    handleMatchingFighter={
                                                                        event?.isEnded
                                                                            ? undefined
                                                                            : handleMatchingFighter
                                                                    }
                                                                    handleRemoveFighter={
                                                                        event?.isEnded
                                                                            ? undefined
                                                                            : handleRemoveFighter
                                                                    }
                                                                    handlePullOutFighter={
                                                                        event?.isEnded
                                                                            ? undefined
                                                                            : handlePullOutFighter
                                                                    }
                                                                    eventDate={
                                                                        event?.dateTime
                                                                    }
                                                                    eventFighter={
                                                                        eventFighter
                                                                    }
                                                                />
                                                            )
                                                        );
                                                    })()}
                                                </div>
                                            </div>
                                        ) : activeTab === "refunded" ? (
                                            <div className="flex flex-col w-full gap-2 h-[calc(100vh-10rem)] md:h-auto md:flex-1 md:min-h-0">
                                                <div className="px-1 pb-1 text-sm opacity-70">
                                                    Fighters refunded with a credit for this event. They
                                                    stay on the public list as unmatched. Use the rejoin
                                                    button to spend a credit and re-activate a match.
                                                </div>
                                                <div className="w-full h-full overflow-y-auto flex flex-col gap-2">
                                                    {(() => {
                                                        if (rosterPool.length === 0) {
                                                            return (
                                                                <div className="w-full h-full flex-center">
                                                                    <p>No refund records.</p>
                                                                </div>
                                                            );
                                                        }
                                                        if (visibleRoster.length === 0) {
                                                            return noMatchesBlock(
                                                                "fighters",
                                                                rosterPool.length
                                                            );
                                                        }
                                                        return visibleRoster.map(
                                                            (eventFighter, index) => {
                                                                const pendingRequestId =
                                                                    pendingRequestByFighterId.get(
                                                                        eventFighter.fighterId
                                                                    );
                                                                const hasPending =
                                                                    !!pendingRequestId;
                                                                return (
                                                                <div
                                                                    key={eventFighter.id ?? index}
                                                                    className="border border-base-300 rounded-lg p-1"
                                                                >
                                                                    <EventFighterListItem
                                                                        handleMatchingFighter={
                                                                            event?.isEnded
                                                                                ? undefined
                                                                                : handleMatchingFighter
                                                                        }
                                                                        handleRejoinWithToken={
                                                                            event?.isEnded ||
                                                                            event?.isTest ||
                                                                            hasPending
                                                                                ? undefined
                                                                                : handleRejoinWithToken
                                                                        }
                                                                        activeTokenCount={
                                                                            tokenCountsByFighterId[
                                                                            eventFighter
                                                                                .fighterId
                                                                            ] ?? 0
                                                                        }
                                                                        eventDate={
                                                                            event?.dateTime
                                                                        }
                                                                        eventFighter={
                                                                            eventFighter
                                                                        }
                                                                    />
                                                                    {hasPending && (
                                                                        <div className="px-2 pt-1 pb-2">
                                                                            <Link
                                                                                to={`${MANAGE_FIGHTER_REQUEST_PATH}/${pendingRequestId}`}
                                                                                className="btn btn-sm btn-info gap-2"
                                                                                title="This fighter submitted a registration request — open and approve it instead of rejoining here."
                                                                            >
                                                                                <Clipboard size={16} />
                                                                                Pending request — Approve in Requests
                                                                            </Link>
                                                                        </div>
                                                                    )}
                                                                    <div className="px-2 pb-1 text-xs opacity-70 flex flex-row flex-wrap gap-x-3 gap-y-1">
                                                                        <span>
                                                                            Status:{" "}
                                                                            <strong className={eventFighter.isRefunded ? "text-warning" : "text-success"}>
                                                                                {eventFighter.isRefunded
                                                                                    ? "Refunded (credit outstanding)"
                                                                                    : "Rejoined / resolved"}
                                                                            </strong>
                                                                        </span>
                                                                        <span>
                                                                            Active credits:{" "}
                                                                            <strong>
                                                                                {tokenCountsByFighterId[
                                                                                    eventFighter.fighterId
                                                                                ] ?? 0}
                                                                            </strong>
                                                                        </span>
                                                                        {!eventFighter.isRefunded &&
                                                                            (tokenCountsByFighterId[
                                                                                eventFighter.fighterId
                                                                            ] ?? 0) > 0 && (
                                                                                <span className="text-info">
                                                                                    Holds an unused credit for a
                                                                                    future event
                                                                                </span>
                                                                            )}
                                                                    </div>
                                                                    {Array.isArray(eventFighter.refundHistory) &&
                                                                        eventFighter.refundHistory.length > 0 && (
                                                                            <ul className="px-2 pb-1 text-xs opacity-60 list-disc list-inside">
                                                                                {eventFighter.refundHistory.map(
                                                                                    (h, i) => (
                                                                                        <li key={i}>
                                                                                            {h.type === "REFUND"
                                                                                                ? "Refunded — credit issued"
                                                                                                : `Rejoined — credit spent${h.via ? ` (${h.via})` : ""}`}
                                                                                            {h.at &&
                                                                                                ` · ${formatFirestoreTimestamp(h.at)}`}
                                                                                        </li>
                                                                                    )
                                                                                )}
                                                                            </ul>
                                                                        )}
                                                                </div>
                                                                );
                                                            }
                                                        );
                                                    })()}
                                                </div>
                                            </div>
                                        ) : (
                                            <div className="flex-center w-full gap-2 h-[calc(100vh-10rem)] md:h-auto md:flex-1 md:min-h-0">
                                                <div className="w-full h-full overflow-y-auto flex flex-col gap-4">
                                                    {pairEntries.length === 0 ? (
                                                        <div className="w-full h-full flex-center">
                                                            <p>
                                                                No matched fighters
                                                                yet.
                                                            </p>
                                                        </div>
                                                    ) : visiblePairs.length ===
                                                        0 ? (
                                                        noMatchesBlock(
                                                            "bouts",
                                                            pairEntries.length
                                                        )
                                                    ) : (
                                                        <>
                                                        {/* Say why dragging is off rather than leaving
                                                            a dead handle on the card. */}
                                                        {!canReorder &&
                                                            !isResultsView &&
                                                            !event?.isEnded && (
                                                                <p className="text-xs opacity-60 px-1">
                                                                    Sort by{" "}
                                                                    <strong>
                                                                        Bout
                                                                        order
                                                                    </strong>{" "}
                                                                    (ascending)
                                                                    to drag
                                                                    bouts into
                                                                    the running
                                                                    order.
                                                                </p>
                                                            )}
                                                        {canReorder &&
                                                            filtersActive && (
                                                                <p className="text-xs opacity-60 px-1">
                                                                    Dragging
                                                                    reorders the
                                                                    whole card,
                                                                    not just the{" "}
                                                                    {
                                                                        visiblePairs.length
                                                                    }{" "}
                                                                    bouts shown.
                                                                </p>
                                                            )}
                                                        {visiblePairs.map(
                                                            (boutEntry) => {
                                                                // pairKey is the identity handleSetWinner /
                                                                // handleSetDraw / handleSetWinBy key off, and
                                                                // index stays the ORIGINAL bout number — the
                                                                // filtered order never renumbers the card.
                                                                const pairKey =
                                                                    boutEntry.pairKey;
                                                                const matchedFighter =
                                                                    boutEntry.pair;
                                                                const index =
                                                                    boutEntry.originalIndex;
                                                                // a = red = LEFT, b = blue = RIGHT, after
                                                                // any corner swap. Everything below (names,
                                                                // WIN buttons, judge strip) reads these.
                                                                const {
                                                                    red: a,
                                                                    blue: b,
                                                                } = resolveCorners(
                                                                    matchedFighter?.fighterA,
                                                                    matchedFighter?.fighterB,
                                                                    event?.boutCorners?.[pairKey]
                                                                );
                                                                // Any card, a draft included, locks the corners.
                                                                const cornersLocked =
                                                                    !!scorecardsByBout[pairKey];
                                                                // Pulled out: the match is cancelled.
                                                                const pulledOut =
                                                                    isPulledOutPair(matchedFighter);
                                                                const showCardControls =
                                                                    canReorder ||
                                                                    !event?.isEnded;

                                                                // boolean variable to check if a and b are the same entry and same weight class
                                                                const sameEntry =
                                                                    a?.entry ===
                                                                    b?.entry;
                                                                const weightClassA =
                                                                    getEventFighterWeightClass(
                                                                        a
                                                                    );
                                                                const weightClassB =
                                                                    getEventFighterWeightClass(
                                                                        b
                                                                    );
                                                                const sameWeightClass =
                                                                    weightClassA ===
                                                                    weightClassB;
                                                                const sameEntrySameWeightClass =
                                                                    sameEntry &&
                                                                    sameWeightClass;

                                                                const isDragging =
                                                                    draggingPairKey ===
                                                                    pairKey;
                                                                const isDropTarget =
                                                                    dropTargetPairKey ===
                                                                        pairKey &&
                                                                    !isDragging;

                                                                return (
                                                                    <div
                                                                        key={pairKey}
                                                                        draggable={
                                                                            canReorder
                                                                        }
                                                                        onDragStart={(e) => {
                                                                            if (!canReorder) return;
                                                                            setDraggingPairKey(pairKey);
                                                                            e.dataTransfer.effectAllowed = "move";
                                                                            // Firefox refuses to start a drag without payload.
                                                                            e.dataTransfer.setData("text/plain", pairKey);
                                                                        }}
                                                                        onDragOver={(e) => {
                                                                            if (!canReorder || !draggingPairKey) return;
                                                                            e.preventDefault();
                                                                            e.dataTransfer.dropEffect = "move";
                                                                            if (dropTargetPairKey !== pairKey) {
                                                                                setDropTargetPairKey(pairKey);
                                                                            }
                                                                        }}
                                                                        onDrop={(e) => {
                                                                            if (!canReorder || !draggingPairKey) return;
                                                                            e.preventDefault();
                                                                            handleReorderBout(draggingPairKey, pairKey);
                                                                            setDraggingPairKey(null);
                                                                            setDropTargetPairKey(null);
                                                                        }}
                                                                        onDragEnd={() => {
                                                                            setDraggingPairKey(null);
                                                                            setDropTargetPairKey(null);
                                                                        }}
                                                                        className={twMerge(
                                                                            "flex-center w-full gap-2 relative p-4 border",
                                                                            CORNER_GRADIENT,
                                                                            canReorder &&
                                                                                "cursor-grab active:cursor-grabbing",
                                                                            isDragging &&
                                                                                "opacity-40",
                                                                            isDropTarget &&
                                                                                "border-primary border-t-4",
                                                                            pulledOut &&
                                                                                "opacity-70 border-warning"
                                                                        )}
                                                                    >
                                                                        <p className="absolute top-0 left-0 font-bold text-sm p-2 border w-8 h-8 flex-center -translate-x-[1px] -translate-y-[1px] leading-none">
                                                                            {index + 1}
                                                                        </p>
                                                                        {/* Touch and keyboard path to the same
                                                                            reorder — HTML5 drag doesn't fire on
                                                                            touch devices. */}
                                                                        {showCardControls && (
                                                                            <div className="absolute top-0 right-0 flex flex-row items-center">
                                                                                {canReorder && (
                                                                                <>
                                                                                <button
                                                                                    className="btn btn-ghost btn-xs btn-square"
                                                                                    title="Move earlier in the card"
                                                                                    aria-label={`Move bout ${index + 1} earlier`}
                                                                                    disabled={index === 0}
                                                                                    onClick={() => handleNudgeBout(pairKey, -1)}
                                                                                >
                                                                                    <ChevronUp size={16} />
                                                                                </button>
                                                                                <button
                                                                                    className="btn btn-ghost btn-xs btn-square"
                                                                                    title="Move later in the card"
                                                                                    aria-label={`Move bout ${index + 1} later`}
                                                                                    disabled={
                                                                                        index ===
                                                                                        orderedPairKeys.length - 1
                                                                                    }
                                                                                    onClick={() => handleNudgeBout(pairKey, 1)}
                                                                                >
                                                                                    <ChevronDown size={16} />
                                                                                </button>
                                                                                </>
                                                                                )}
                                                                                {/* Swap corners: free until any judge has a
                                                                                    card for this bout, then locked (the
                                                                                    server enforces the same rule). */}
                                                                                <button
                                                                                    className="btn btn-ghost btn-xs btn-square"
                                                                                    title={
                                                                                        cornersLocked
                                                                                            ? "Judges have scored this match, so the corners are locked"
                                                                                            : "Swap red and blue corners"
                                                                                    }
                                                                                    aria-label={
                                                                                        cornersLocked
                                                                                            ? `Corners locked for bout ${index + 1}`
                                                                                            : `Swap red and blue corners for bout ${index + 1}`
                                                                                    }
                                                                                    disabled={
                                                                                        cornersLocked ||
                                                                                        swappingPairKey ===
                                                                                            pairKey
                                                                                    }
                                                                                    onClick={() =>
                                                                                        handleSwapCorners(
                                                                                            pairKey
                                                                                        )
                                                                                    }
                                                                                >
                                                                                    {swappingPairKey ===
                                                                                    pairKey ? (
                                                                                        <span className="loading loading-spinner loading-xs" />
                                                                                    ) : cornersLocked ? (
                                                                                        <Lock size={16} />
                                                                                    ) : (
                                                                                        <ArrowLeftRight size={16} />
                                                                                    )}
                                                                                </button>
                                                                                {canReorder && (
                                                                                <span className="px-1 opacity-40">
                                                                                    <GripVertical size={16} />
                                                                                </span>
                                                                                )}
                                                                            </div>
                                                                        )}
                                                                        <div className="flex flex-row gap-2 w-full justify-between">
                                                                            <div className="flex flex-col items-center gap-2 p-2 w-full">
                                                                                <div className="w-24 aspect-square">
                                                                                    <img
                                                                                        className="w-full h-full object-center object-cover aspect-square"
                                                                                        src={
                                                                                            a
                                                                                                ?.fighter
                                                                                                ?.fighterPicture
                                                                                        }
                                                                                        alt=""
                                                                                    />
                                                                                </div>
                                                                                <p className="font-bold">
                                                                                    {
                                                                                        a
                                                                                            ?.fighter
                                                                                            ?.name
                                                                                    }
                                                                                </p>
                                                                            </div>
                                                                            <div className="flex-center gap-2 w-full">
                                                                                {!sameEntrySameWeightClass && (
                                                                                    <>
                                                                                        <p className="self-start text-sm leading-none font-bold">
                                                                                            {
                                                                                                a?.entry
                                                                                            }
                                                                                        </p>
                                                                                        <p className="self-start text-sm leading-none font-bold">
                                                                                            {(() => {
                                                                                                const wc =
                                                                                                    getEventFighterWeightClass(
                                                                                                        a
                                                                                                    );
                                                                                                return wc
                                                                                                    ? `${wc}: ${weightClassesName[wc]}`
                                                                                                    : "Unknown Weight Class";
                                                                                            })()}
                                                                                        </p>
                                                                                    </>
                                                                                )}
                                                                                <div className="divider m-0">
                                                                                    vs
                                                                                </div>
                                                                                <p
                                                                                    className={twMerge(
                                                                                        "text-sm leading-none font-bold",
                                                                                        !sameEntrySameWeightClass &&
                                                                                        "self-end"
                                                                                    )}
                                                                                >
                                                                                    {
                                                                                        b?.entry
                                                                                    }
                                                                                </p>
                                                                                <p
                                                                                    className={twMerge(
                                                                                        "text-sm leading-none font-bold",
                                                                                        !sameEntrySameWeightClass &&
                                                                                        "self-end"
                                                                                    )}
                                                                                >
                                                                                    {(() => {
                                                                                        const wc =
                                                                                            getEventFighterWeightClass(
                                                                                                b
                                                                                            );
                                                                                        return wc
                                                                                            ? `${wc}: ${weightClassesName[wc]}`
                                                                                            : "Unknown Weight Class";
                                                                                    })()}
                                                                                </p>
                                                                                {/* Red's class sits above "vs", blue's
                                                                                    below; flag a cross-class bout. */}
                                                                                {!sameWeightClass && (
                                                                                    <span className="badge badge-warning badge-sm whitespace-nowrap">
                                                                                        2 CLASSES
                                                                                    </span>
                                                                                )}
                                                                            </div>
                                                                            <div className="flex flex-col items-center gap-2 p-2 w-full">
                                                                                <div className="w-24 aspect-square">
                                                                                    <img
                                                                                        className="w-full h-fulll object-center object-cover aspect-square"
                                                                                        src={
                                                                                            b
                                                                                                ?.fighter
                                                                                                ?.fighterPicture
                                                                                        }
                                                                                        alt=""
                                                                                    />
                                                                                </div>
                                                                                <p className="font-bold">
                                                                                    {
                                                                                        b
                                                                                            ?.fighter
                                                                                            ?.name
                                                                                    }
                                                                                </p>
                                                                            </div>
                                                                        </div>
                                                                        {/* Judges' cards + suggested result (live). */}
                                                                        <BoutJudgeStrip
                                                                            judges={eventJudges}
                                                                            cardsByJudge={scorecardsByBout[pairKey]}
                                                                            fighterA={a}
                                                                            fighterB={b}
                                                                            matchedFighter={matchedFighter}
                                                                            readOnly={!!event?.isEnded || pulledOut}
                                                                            onAccept={(result) =>
                                                                                handleAcceptJudges(pairKey, result)
                                                                            }
                                                                            eventId={eventId}
                                                                            pairKey={pairKey}
                                                                        />
                                                                        {pulledOut ? (
                                                                            <div className="w-full border border-warning text-warning px-3 py-2 text-sm font-bold uppercase tracking-wide text-center">
                                                                                Pulled out · match cancelled. No result is recorded. Restore the fighter to bring it back.
                                                                            </div>
                                                                        ) : (
                                                                        <>
                                                                        <div className="flex flex-row gap-2 w-full justify-between">
                                                                            <button
                                                                                className={twMerge(
                                                                                    "flex-1 btn-sm btn",
                                                                                    matchedFighter?.winId ===
                                                                                        a?.fighterId
                                                                                        ? "btn-primary"
                                                                                        : ""
                                                                                )}
                                                                                onClick={() =>
                                                                                    handleSetWinner(
                                                                                        pairKey,
                                                                                        a.fighterId,
                                                                                        b.fighterId
                                                                                    )
                                                                                }
                                                                            >
                                                                                WIN
                                                                            </button>
                                                                            <button
                                                                                className={twMerge(
                                                                                    "flex-1 btn-sm btn",
                                                                                    matchedFighter.isDraw
                                                                                        ? "btn-primary"
                                                                                        : ""
                                                                                )}
                                                                                onClick={() =>
                                                                                    handleSetDraw(
                                                                                        pairKey
                                                                                    )
                                                                                }
                                                                            >
                                                                                DRAW
                                                                            </button>
                                                                            <button
                                                                                className={twMerge(
                                                                                    "flex-1 btn-sm btn",
                                                                                    matchedFighter?.winId ===
                                                                                        b?.fighterId
                                                                                        ? "btn-primary"
                                                                                        : ""
                                                                                )}
                                                                                onClick={() =>
                                                                                    handleSetWinner(
                                                                                        pairKey,
                                                                                        b.fighterId,
                                                                                        a.fighterId
                                                                                    )
                                                                                }
                                                                            >
                                                                                WIN
                                                                            </button>
                                                                        </div>
                                                                        <div className="flex flex-row gap-2 w-full justify-between">
                                                                            <button
                                                                                className={twMerge(
                                                                                    "flex-1 btn btn-sm",
                                                                                    matchedFighter?.winBy ===
                                                                                        "tko"
                                                                                        ? "btn-primary"
                                                                                        : ""
                                                                                )}
                                                                                onClick={() =>
                                                                                    handleSetWinBy(
                                                                                        pairKey,
                                                                                        "tko"
                                                                                    )
                                                                                }
                                                                            >
                                                                                TKO
                                                                            </button>
                                                                            <button
                                                                                className={twMerge(
                                                                                    "flex-1 btn btn-sm",
                                                                                    matchedFighter?.winBy ===
                                                                                        "ko"
                                                                                        ? "btn-primary"
                                                                                        : ""
                                                                                )}
                                                                                onClick={() =>
                                                                                    handleSetWinBy(
                                                                                        pairKey,
                                                                                        "ko"
                                                                                    )
                                                                                }
                                                                            >
                                                                                KO
                                                                            </button>
                                                                            <button
                                                                                className={twMerge(
                                                                                    "flex-1 btn btn-sm",
                                                                                    matchedFighter?.winBy ===
                                                                                        "submission"
                                                                                        ? "btn-primary"
                                                                                        : ""
                                                                                )}
                                                                                onClick={() =>
                                                                                    handleSetWinBy(
                                                                                        pairKey,
                                                                                        "submission"
                                                                                    )
                                                                                }
                                                                            >
                                                                                SUBMISSION
                                                                            </button>
                                                                            <button
                                                                                className={twMerge(
                                                                                    "flex-1 btn btn-sm",
                                                                                    matchedFighter?.winBy ===
                                                                                        "decision"
                                                                                        ? "btn-primary"
                                                                                        : ""
                                                                                )}
                                                                                onClick={() =>
                                                                                    handleSetWinBy(
                                                                                        pairKey,
                                                                                        "decision"
                                                                                    )
                                                                                }
                                                                            >
                                                                                DECISION
                                                                            </button>
                                                                        </div>
                                                                        </>
                                                                        )}
                                                                    </div>
                                                                );
                                                            }
                                                        )}
                                                        </>
                                                    )}
                                                </div>

                                                {/* Why END EVENT is disabled: a disabled
                                                    button shows no tooltip, so say it here. */}
                                                {endEventBlocked && (
                                                    <p
                                                        id="end-event-hint"
                                                        className="w-full px-1 text-sm font-semibold text-warning"
                                                        title={endEventBlockedWhy.title}
                                                        aria-live="polite"
                                                    >
                                                        {endEventBlockedWhy.hint}
                                                    </p>
                                                )}
                                                <div className="flex flex-row gap-4 w-full">
                                                    <button
                                                        className={twMerge(
                                                            "btn flex-1",
                                                            event?.isEnded ||
                                                                event?.isTest ||
                                                                endEventBlocked
                                                                ? "btn-disabled"
                                                                : "btn-primary"
                                                        )}
                                                        onClick={handleEndEvent}
                                                        disabled={
                                                            event?.isEnded ||
                                                            event?.isTest ||
                                                            endEventBlocked
                                                        }
                                                        title={
                                                            event?.isTest
                                                                ? "A test event can't be ended. Turn off Test event first."
                                                                : endEventBlocked
                                                                  ? endEventBlockedWhy.title
                                                                  : undefined
                                                        }
                                                        aria-describedby={
                                                            endEventBlocked
                                                                ? "end-event-hint"
                                                                : undefined
                                                        }
                                                    >
                                                        {event?.isEnded ? (
                                                            "EVENT ENDED"
                                                        ) : event?.isTest ? (
                                                            <>
                                                                <FlaskConical size={18} />
                                                                TEST EVENT
                                                            </>
                                                        ) : (
                                                            "END EVENT"
                                                        )}
                                                    </button>
                                                    {!event?.isEnded &&
                                                        (eventJudges?.length ?? 0) > 0 && (
                                                            <button
                                                                className="btn btn-outline"
                                                                onClick={handleFillFromJudges}
                                                                title="Select WIN / DRAW and TKO / KO / DECISION on every match from the judges' cards. Your own picks are kept. Nothing is saved until END EVENT."
                                                            >
                                                                <Gavel size={18} />
                                                                <span className="hidden sm:inline">
                                                                    Use judges' results
                                                                </span>
                                                            </button>
                                                        )}
                                                    <button
                                                        className="btn btn-outline"
                                                        onClick={handleExportFightCard}
                                                        disabled={fightCardBusy}
                                                        title="Download the fight card as an Excel sheet to edit and print"
                                                    >
                                                        {fightCardBusy ? (
                                                            <span className="loading loading-spinner loading-sm" />
                                                        ) : (
                                                            <FileSpreadsheet size={18} />
                                                        )}
                                                        <span className="hidden sm:inline">
                                                            Export fight card
                                                        </span>
                                                    </button>
                                                    <button
                                                        className="btn btn-outline btn-square"
                                                        onClick={
                                                            handleCopyMatchedFighters
                                                        }
                                                    >
                                                        <Clipboard />
                                                    </button>
                                                </div>
                                            </div>
                                        )}
                                </div>
                            )}
                        </div>
                    </div>
                </>
            )}

            <dialog id="end-event-dialog" className="modal">
                <div className="modal-box border">
                    <h3 className="font-bold text-lg">Confirm End Event</h3>
                    <p className="py-4">
                        Are you sure you want to end this event? This action
                        will:
                    </p>
                    <ul className="list-disc list-inside py-2 space-y-1">
                        <li>Mark the event as ended</li>
                        <li>
                            Update all fighter records based on match results
                        </li>
                        <li>Store final event results</li>
                        <li>This action cannot be undone</li>
                    </ul>
                    <div className="modal-action">
                        <form method="dialog">
                            <button className="btn btn-ghost mr-2">
                                Cancel
                            </button>
                            <button
                                className="btn btn-error"
                                onClick={handleConfirmEndEvent}
                            >
                                End Event
                            </button>
                        </form>
                    </div>
                </div>
                <form method="dialog" className="modal-backdrop">
                    <button>close</button>
                </form>
            </dialog>

            <dialog
                id="match-fighter-modal"
                className={twMerge(
                    "modal",
                    !!selectedEventFighter ? "modal-open" : ""
                )}
                ref={matchFighterModal}
            >
                <div className="modal-box max-w-[50rem] border">
                    <div className="w-full flex flex-row justify-between items-center">
                        <h3 className="font-bold text-lg">Match Fihgter:</h3>
                        <button
                            className="btn btn-square btn-ghost"
                            onClick={handleEndMatchingFighter}
                        >
                            <X />
                        </button>
                    </div>
                    {!selectedEventFighter ? (
                        <p> Something went wrong.</p>
                    ) : (
                        <div className="w-full flex flex-row gap-4">
                            <div className="flex-center w-full h-full justify-around gap-2">
                                <FighterMatchingCard
                                    eventFighter={selectedEventFighter}
                                />
                                <div className="divider m-0">vs</div>
                                {!!matchingEventFighter ? (
                                    <div className="relative w-full">
                                        <FighterMatchingCard
                                            eventFighter={matchingEventFighter}
                                        />
                                        {!!matchingEventFighter?.matchWith &&
                                            matchingEventFighter?.matchWith !==
                                            selectedEventFighter.fighterId ? (
                                            <div
                                                data-tip="This fighter has been paired with another. If they are matched with the current one, the original pairing will be automatically removed."
                                                className="absolute top-0 right-0 p-1 rounded-full border-2 border-error tooltip tooltip-left"
                                            >
                                                <Skull className="text-error pb-[.75px]" />
                                            </div>
                                        ) : null}
                                    </div>
                                ) : (
                                    <div className="flex-center aspect-video w-full text-3xl">
                                        ?
                                    </div>
                                )}
                                {!!selectedEventFighter &&
                                    !!matchingEventFighter &&
                                    selectedEventFighter?.matchWith ===
                                    matchingEventFighter.fighterId ? (
                                    <button
                                        className="btn btn-primary w-full disabled:!bg-gray-400"
                                        onClick={handleUnMatchEventFighter}
                                    >
                                        UNMATCH!
                                    </button>
                                ) : (
                                    <button
                                        className="btn btn-primary w-full disabled:!bg-gray-400"
                                        disabled={!matchingChanged}
                                        onClick={handleMatchEventFighter}
                                    >
                                        MATCH!
                                    </button>
                                )}
                            </div>
                            <div className="flex flex-col w-full h-[35.5rem] overflow-y-auto relative gap-2">
                                <div>
                                    <input
                                        type="text"
                                        className="input input-bordered input-sm w-full"
                                        placeholder="Search for a fighter"
                                        value={fighterSearchTerm}
                                        onChange={(e) => setFighterSearchTerm(e.target.value)}
                                    />
                                </div>
                                <p className="sticky top-0 right-0 font-bold bg-base-100 w-full py-2 z-50">
                                    Same Entry ({selectedEventFighter?.entry}):
                                </p>



                                {getRecommendedOpponents(
                                    selectedEventFighter,
                                    eventFighters.filter(
                                        (ef) =>
                                            ef.entry ===
                                            selectedEventFighter.entry &&
                                            !ef.isPulledOut &&
                                            (!fighterSearchTerm.trim() ||
                                                (ef?.fighter?.name || "").toLowerCase().includes(fighterSearchTerm.toLowerCase()))
                                    ),
                                    selectedEventFighter.entry,
                                    getEventFighterWeightClass
                                ).map((fighter, index) => {
                                    const eventFighter = fighter; // fighter is already an event fighter
                                    const fighterData =
                                        eventFighter?.fighter ?? fighter;
                                    const record =
                                        fighterData?.records[
                                        selectedEventFighter.entry
                                        ];
                                    return (
                                        <div
                                            className="flex flex-col w-full cursor-pointer hover:bg-base-200"
                                            key={index}
                                            onClick={() =>
                                                handleSelectMathingEventFighter(
                                                    eventFighter
                                                )
                                            }
                                        >
                                            <div className="flex w-full gap-2">
                                                <div className="aspect-square w-16">
                                                    <img
                                                        className="w-full h-full object-cover object-center"
                                                        src={
                                                            fighterData?.fighterPicture
                                                        }
                                                        alt=""
                                                    />
                                                </div>
                                                <div className="w-full min-w-0">
                                                    <p className="font-bold flex gap-2 items-center">
                                                        <span className="truncate">
                                                            {fighterData?.name}
                                                        </span>
                                                        {eventFighter?.fighter?.gender?.toUpperCase() ===
                                                            "M" ? (
                                                            <Mars className="text-blue-300 flex-shrink-0" />
                                                        ) : eventFighter?.fighter?.gender?.toUpperCase() ===
                                                            "F" ? (
                                                            <Venus className="text-pink-300 flex-shrink-0" />
                                                        ) : (
                                                            <span className="flex-shrink-0">?</span>
                                                        )}
                                                    </p>
                                                    <p className="text-sm">
                                                        Weight:{" "}
                                                        {getEventFighterWeightClass(
                                                            eventFighter
                                                        ) ?? "?"}
                                                        , Age:{" "}
                                                        {eventFighter?.fighter
                                                            ?.age ?? "?"}
                                                    </p>
                                                    <p className="text-sm">
                                                        Record:{" "}
                                                        {record?.win ?? "?"} |{" "}
                                                        {record?.loss ?? "?"} |{" "}
                                                        {record?.draw ?? "?"}
                                                    </p>
                                                </div>
                                                <p className="h-full flex-center text-nowrap pr-2 text-sm opacity-70">
                                                    {(fighter?.score ?? "?") ===
                                                        -Infinity ? (
                                                        <div
                                                            className="tooltip tooltip-left"
                                                            data-tip={
                                                                fighter?.reason ??
                                                                "???"
                                                            }
                                                        >
                                                            <TriangleAlert className="text-error" />
                                                        </div>
                                                    ) : (
                                                        `${fighter?.score}%`
                                                    )}
                                                </p>
                                            </div>
                                        </div>
                                    );
                                })}
                                <b />

                                <p className="sticky top-0 right-0 font-bold bg-base-100 py-2 z-50">
                                    Other Entry:
                                </p>
                                {eventFighters
                                    .filter(
                                        (ef) =>
                                            ef.entry !==
                                            selectedEventFighter.entry &&
                                            (!fighterSearchTerm.trim() ||
                                                (ef?.fighter?.name || "").toLowerCase().includes(fighterSearchTerm.toLowerCase()))
                                    )
                                    .map((eventFighter, index) => {
                                        const fighter = eventFighter?.fighter;
                                        const record =
                                            fighter?.records[
                                            eventFighter.entry
                                            ];

                                        return (
                                            <div
                                                className="flex flex-col w-full cursor-pointer hover:bg-base-200"
                                                key={index}
                                                onClick={() =>
                                                    handleSelectMathingEventFighter(
                                                        eventFighter
                                                    )
                                                }
                                            >
                                                <div className="flex w-full gap-2">
                                                    <div className="aspect-square w-16">
                                                        <img
                                                            className="w-full h-full object-cover object-center"
                                                            src={
                                                                fighter?.fighterPicture
                                                            }
                                                            alt=""
                                                        />
                                                    </div>
                                                    <div className="w-full min-w-0">
                                                        <p className="font-bold flex gap-2 items-center">
                                                            <span className="truncate">
                                                                {fighter?.name}
                                                            </span>
                                                            {eventFighter?.fighter?.gender?.toUpperCase() ===
                                                                "M" ? (
                                                                <Mars className="text-blue-300 flex-shrink-0" />
                                                            ) : eventFighter?.fighter?.gender?.toUpperCase() ===
                                                                "F" ? (
                                                                <Venus className="text-pink-300 flex-shrink-0" />
                                                            ) : (
                                                                <span className="flex-shrink-0">?</span>
                                                            )}
                                                        </p>
                                                        <p className="text-sm">
                                                            Weight:{" "}
                                                            {getEventFighterWeightClass(
                                                                eventFighter
                                                            ) ?? "?"}
                                                            , Age:{" "}
                                                            {eventFighter
                                                                ?.fighter
                                                                ?.age ?? "?"}
                                                        </p>
                                                        <p className="text-sm">
                                                            Record:{" "}
                                                            {record?.win ?? "?"}{" "}
                                                            |{" "}
                                                            {record?.loss ??
                                                                "?"}{" "}
                                                            |{" "}
                                                            {record?.draw ??
                                                                "?"}
                                                        </p>
                                                    </div>
                                                    <p className="h-full flex-center text-nowrap pr-2 text-sm opacity-70">
                                                        {eventFighter?.entry}
                                                    </p>
                                                </div>
                                            </div>
                                        );
                                    })}
                                <b />

                                <p className="sticky top-0 right-0 font-bold bg-base-100 py-2 z-50">
                                    Unregistered Fighters (Not in Event):
                                </p>
                                {(() => {
                                    // Transform availableFighters to event-fighter-like structure for scoring
                                    const unregisteredWithEntry = availableFighters
                                        .filter((fighter) => {
                                            // Filter by search term
                                            if (
                                                fighterSearchTerm.trim() &&
                                                !(fighter?.name || "")
                                                    .toLowerCase()
                                                    .includes(
                                                        fighterSearchTerm.toLowerCase()
                                                    )
                                            ) {
                                                return false;
                                            }
                                            // Filter by entry type - only show fighters who have records for this entry
                                            if (
                                                selectedEventFighter?.entry &&
                                                !fighter?.records?.[
                                                selectedEventFighter.entry
                                                ]
                                            ) {
                                                return false;
                                            }
                                            return true;
                                        })
                                        .map((fighter) => ({
                                            ...fighter,
                                            entry: selectedEventFighter?.entry,
                                            fighterId: fighter.id,
                                            eventWeightClass: fighter.weightClass,
                                            fighter: fighter, // Self-reference for consistent access
                                        }));

                                    const scoredUnregistered = getRecommendedOpponents(
                                        selectedEventFighter,
                                        unregisteredWithEntry,
                                        selectedEventFighter?.entry,
                                        getEventFighterWeightClass
                                    );

                                    if (scoredUnregistered.length === 0) {
                                        return (
                                            <p className="text-sm opacity-70 py-4 text-center">
                                                No matching fighters available
                                            </p>
                                        );
                                    }

                                    return scoredUnregistered.map(
                                        (fighterWrapper, index) => {
                                            const fighter =
                                                fighterWrapper?.fighter ??
                                                fighterWrapper;
                                            const record =
                                                fighter?.records?.[
                                                selectedEventFighter?.entry
                                                ] ?? {
                                                    win: 0,
                                                    loss: 0,
                                                    draw: 0,
                                                };
                                            const weightClass =
                                                getEventFighterWeightClass(
                                                    fighterWrapper
                                                );


                                            // http://localhost:6969/register-event/5BFa9ob8BLDPuua0HUvt
                                            const registerEventUrl = `${window.location.origin}${REGISTER_EVENT_PATH}/${event?.id}`;

                                            // Generate WhatsApp message
                                            const message = encodeURIComponent(
                                                `Hi ${fighter?.name || "there"}, I noticed your profile matches well with an upcoming event *${event?.name || "our event"}*. \n\nIf you are interested in registering for the event, please click this link to register: ${registerEventUrl}`
                                            );

                                            const formattedPhoneNumber = formatPhoneNumberForWhatsApp(fighter.phoneNumber);
                                            // https://wa.me/phonenumber?text=urlencodedmessage
                                            const whatsappUrl = fighter?.phoneNumber
                                                ? `https://wa.me/${formattedPhoneNumber}?text=${message}`
                                                : null;

                                            return (
                                                <div
                                                    className="flex flex-col w-full hover:bg-base-200"
                                                    key={`unregistered-${fighter?.id || index}`}
                                                >
                                                    <div className="flex w-full gap-2">
                                                        <div className="aspect-square w-16">
                                                            <img
                                                                className="w-full h-full object-cover object-center"
                                                                src={
                                                                    fighter?.fighterPicture
                                                                }
                                                                alt=""
                                                            />
                                                        </div>
                                                        <div className="w-full min-w-0">
                                                            <p className="font-bold flex gap-2 items-center">
                                                                <span className="truncate">
                                                                    {fighter?.name}
                                                                </span>
                                                                {fighter?.gender?.toUpperCase() ===
                                                                    "M" ? (
                                                                    <Mars className="text-blue-300 flex-shrink-0" />
                                                                ) : fighter?.gender?.toUpperCase() ===
                                                                    "F" ? (
                                                                    <Venus className="text-pink-300 flex-shrink-0" />
                                                                ) : (
                                                                    <span className="flex-shrink-0">?</span>
                                                                )}
                                                            </p>
                                                            <p className="text-sm">
                                                                Weight:{" "}
                                                                {weightClass ?? "?"}
                                                                , Age:{" "}
                                                                {fighter?.age ?? "?"}
                                                                , Height:{" "}
                                                                {fighter?.height ?? "?"}
                                                            </p>
                                                            <p className="text-sm">
                                                                Record:{" "}
                                                                {record?.win ??
                                                                    "?"}{" "}
                                                                |{" "}
                                                                {record?.loss ??
                                                                    "?"}{" "}
                                                                |{" "}
                                                                {record?.draw ??
                                                                    "?"}
                                                            </p>
                                                        </div>
                                                        <div className="h-full flex items-center justify-center flex-col gap-2">
                                                            <p className="text-nowrap text-sm opacity-70">
                                                                {(fighterWrapper?.score ??
                                                                    "?") ===
                                                                    -Infinity ? (
                                                                    <div
                                                                        className="tooltip tooltip-left"
                                                                        data-tip={
                                                                            fighterWrapper?.reason ??
                                                                            "???"
                                                                        }
                                                                    >
                                                                        <TriangleAlert className="text-error" />
                                                                    </div>
                                                                ) : (
                                                                    `${fighterWrapper?.score ?? "?"}%`
                                                                )}
                                                            </p>
                                                            {whatsappUrl ? (
                                                                <a
                                                                    href={
                                                                        whatsappUrl
                                                                    }
                                                                    target="_blank"
                                                                    rel="noopener noreferrer"
                                                                    className="flex items-center justify-center btn btn-sm btn-ghost btn-circle tooltip tooltip-left"
                                                                    data-tip="Contact via WhatsApp"
                                                                    onClick={(
                                                                        e
                                                                    ) =>
                                                                        e.stopPropagation()
                                                                    }
                                                                >
                                                                    <FaWhatsapp className="text-green-500 w-5 h-5" />
                                                                </a>
                                                            ) : (
                                                                <div
                                                                    className="btn btn-sm btn-ghost btn-circle tooltip tooltip-left btn-disabled"
                                                                    data-tip="No phone number"
                                                                >
                                                                    <FaWhatsapp className="text-gray-400 w-5 h-5" />
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                            );
                                        }
                                    );
                                })()}
                            </div>
                        </div>
                    )}
                </div>
                <form
                    method="dialog"
                    className="modal-backdrop back"
                    onClick={handleEndMatchingFighter}
                >
                    <button>close</button>
                </form>
            </dialog>

            {/* Add Fighter Modal */}
            <dialog id="add-fighter-modal" className="modal">
                <div className="modal-box border max-w-2xl">
                    <h3 className="font-bold text-lg mb-4">
                        Manually Add Fighter to Event
                    </h3>

                    {/* Fighter Search */}
                    <div className="form-control w-full mb-4">
                        <label className="label">
                            <span className="label-text">Select Fighter *</span>
                        </label>
                        <SelectInput
                            options={availableFighters.map(
                                (f) => `${f.name} (${f.email || "No Email"})`
                            )}
                            placeholder="Search fighter by name..."
                            value={
                                selectedFighterForAdd
                                    ? `${selectedFighterForAdd.name} (${selectedFighterForAdd.email ||
                                    "No Email"
                                    })`
                                    : ""
                            }
                            onSelect={(option) => {
                                // Find fighter by matching the option string "name (email)"
                                const fighter = availableFighters.find(
                                    (f) =>
                                        `${f.name} (${f.email || "No Email"
                                        })` === option
                                );
                                if (fighter) {
                                    setSelectedFighterForAdd(fighter);
                                    // Automatically set weight class to fighter's weight class
                                    if (fighter?.weightClass) {
                                        setSelectedEventWeightClassForAdd(
                                            fighter.weightClass
                                        );
                                    }
                                }
                            }}
                        />
                    </div>

                    {/* Entry Selection */}
                    <div className="form-control w-full mb-4">
                        <label className="label">
                            <span className="label-text">Entry Type *</span>
                        </label>
                        <SelectInput
                            options={fighterEnties.map(
                                (entry) =>
                                    fighterEntryDisplayMapping[entry] || entry
                            )}
                            placeholder="Select entry type..."
                            value={
                                selectedEntryForAdd
                                    ? fighterEntryDisplayMapping[
                                    selectedEntryForAdd
                                    ] || selectedEntryForAdd
                                    : ""
                            }
                            onSelect={(option) => {
                                // Find entry by matching the display name
                                const entry = fighterEnties.find(
                                    (e) =>
                                        (fighterEntryDisplayMapping[e] || e) ===
                                        option
                                );
                                if (entry) {
                                    setSelectedEntryForAdd(entry);
                                }
                            }}
                        />
                    </div>

                    {/* Event Weight Class (Optional) */}
                    <div className="form-control w-full mb-4">
                        <label className="label">
                            <span className="label-text">
                                Event Weight Class (Optional)
                            </span>
                        </label>
                        <SelectInput
                            options={[
                                "None (Use Fighter's Default)",
                                ...Object.keys(weightClasses).map(
                                    (key) => `${key}: ${weightClasses[key]}`
                                ),
                            ]}
                            placeholder="Leave empty for default"
                            value={
                                selectedEventWeightClassForAdd
                                    ? `${selectedEventWeightClassForAdd}: ${weightClasses[
                                    selectedEventWeightClassForAdd
                                    ] || ""
                                    }`
                                    : ""
                            }
                            onSelect={(option) => {
                                // Check if "None" option was selected
                                if (
                                    !option ||
                                    option === "" ||
                                    option.startsWith("None")
                                ) {
                                    setSelectedEventWeightClassForAdd(null);
                                    return;
                                }
                                // Extract weight class key from the option string "KEY: Description"
                                const weightClassMatch =
                                    option.match(/^([A-K]):/);
                                if (weightClassMatch && weightClassMatch[1]) {
                                    setSelectedEventWeightClassForAdd(
                                        weightClassMatch[1]
                                    );
                                }
                            }}
                        />
                    </div>

                    {/* Actions */}
                    <div className="modal-action">
                        <form method="dialog">
                            <button
                                className="btn btn-ghost mr-2"
                                onClick={() => {
                                    setSelectedFighterForAdd(null);
                                    setSelectedEntryForAdd(null);
                                    setSelectedEventWeightClassForAdd(null);
                                }}
                            >
                                Cancel
                            </button>
                            <button
                                className="btn btn-primary"
                                onClick={handleAddFighterToEvent}
                                disabled={
                                    !selectedFighterForAdd ||
                                    !selectedEntryForAdd
                                }
                            >
                                Add Fighter
                            </button>
                        </form>
                    </div>
                </div>
                <form method="dialog" className="modal-backdrop">
                    <button>close</button>
                </form>
            </dialog>

            {/* Remove Fighter Confirmation Modal */}
            <dialog id="pull-out-blocked-dialog" className="modal">
                <div className="modal-box border">
                    <h3 className="font-bold text-lg">
                        Cannot Pull Out Matched Fighter
                    </h3>
                    <p className="py-4">
                        <strong>
                            {pullOutBlockedFighter?.fighter?.name || "This fighter"}
                        </strong>{" "}
                        is currently matched. Please unmatch this fighter first
                        before pulling them out.
                    </p>
                    <div className="modal-action">
                        <form method="dialog">
                            <button
                                className="btn btn-primary"
                                onClick={() => setPullOutBlockedFighter(null)}
                            >
                                OK
                            </button>
                        </form>
                    </div>
                </div>
                <form method="dialog" className="modal-backdrop">
                    <button onClick={() => setPullOutBlockedFighter(null)}>
                        close
                    </button>
                </form>
            </dialog>

            <dialog id="remove-fighter-dialog" className="modal">
                <div className="modal-box border">
                    <h3 className="font-bold text-lg">
                        Remove Fighter from Event
                    </h3>
                    <p className="py-4">
                        Are you sure you want to remove{" "}
                        <strong>
                            {fighterToRemove?.fighter?.name || "this fighter"}
                        </strong>{" "}
                        from this event?
                    </p>
                    <div className="alert alert-error font-bold">
                        <p className="text-sm">
                            This will remove the fighter from the event but will
                            not affect any pending registration requests.
                        </p>
                    </div>
                    <div className="modal-action">
                        <form method="dialog">
                            <button
                                className="btn btn-ghost mr-2"
                                onClick={() => setFighterToRemove(null)}
                            >
                                Cancel
                            </button>
                            <button
                                className="btn btn-error"
                                onClick={handleConfirmRemoveFighter}
                            >
                                Remove Fighter
                            </button>
                        </form>
                    </div>
                </div>
                <form method="dialog" className="modal-backdrop">
                    <button>close</button>
                </form>
            </dialog>

            {/* Weight for this event only — never writes the fighter profile. */}
            {!!weightEditFighter && (
                <div className="modal modal-open">
                    <div className="modal-box border max-w-md">
                        <div className="flex flex-row justify-between items-center gap-2">
                            <h3 className="font-bold text-lg">
                                Weight class for this event
                            </h3>
                            <button
                                className="btn btn-ghost btn-square btn-sm"
                                onClick={() => setWeightEditFighter(null)}
                            >
                                <X />
                            </button>
                        </div>
                        <p className="text-sm opacity-70 mt-1">
                            {weightEditFighter?.fighter?.name} ·{" "}
                            {event?.name || "this event"}
                        </p>
                        <p className="text-xs opacity-60 mt-1">
                            {weightEditFighter?.eventWeightClass
                                ? `Currently registered in ${
                                      weightEditFighter.eventWeightClass
                                  } ${
                                      weightClassesName[
                                          weightEditFighter.eventWeightClass
                                      ]
                                  }.`
                                : "No class recorded on this registration — the roster falls back to their profile class."}
                        </p>

                        <div className="mt-4">
                            <WeightClassPicker
                                value={weightEditClass}
                                baselineClass={
                                    getEventFighterWeightClass(
                                        weightEditFighter
                                    ) || null
                                }
                                onChange={setWeightEditClass}
                                disabled={savingWeight}
                            />
                        </div>

                        {/* Changing the class of a booked bout has a cost —
                            say so before the admin commits, not after. */}
                        {!!weightEditFighter?.matchWith &&
                            weightEditClass !==
                                weightEditFighter?.eventWeightClass && (
                                <div className="alert alert-warning rounded-none mt-4 text-sm">
                                    <TriangleAlert size={18} />
                                    <span>
                                        Matched vs{" "}
                                        {eventFighters?.find(
                                            (ef) =>
                                                ef.fighterId ===
                                                weightEditFighter.matchWith
                                        )?.fighter?.name || "an opponent"}
                                        . The pairing is kept and flagged for
                                        rematch review — never unmatched
                                        automatically.
                                    </span>
                                </div>
                            )}

                        <p className="text-xs opacity-60 mt-4">
                            This changes this event only. The fighter's profile
                            weight is not touched.
                        </p>

                        <div className="flex flex-row gap-2 mt-4">
                            <button
                                className="btn btn-ghost flex-1"
                                disabled={savingWeight}
                                onClick={() => setWeightEditFighter(null)}
                            >
                                Cancel
                            </button>
                            <button
                                className="btn btn-primary flex-1"
                                disabled={savingWeight}
                                onClick={handleConfirmWeightEdit}
                            >
                                {savingWeight ? (
                                    <span className="loading loading-spinner loading-sm" />
                                ) : (
                                    "Save class"
                                )}
                            </button>
                        </div>
                    </div>
                    <div
                        className="modal-backdrop"
                        onClick={
                            savingWeight
                                ? undefined
                                : () => setWeightEditFighter(null)
                        }
                    />
                </div>
            )}

            <dialog id="refund-dialog" className="modal">
                <div className="modal-box border">
                    <h3 className="font-bold text-lg">Refund with Credit</h3>
                    <p className="py-4">
                        Refund{" "}
                        <strong>
                            {fighterToRefund?.fighter?.name || "this fighter"}
                        </strong>{" "}
                        for this event? This issues them{" "}
                        <strong>1 MFL fight credit</strong> and records the
                        refund. The fighter <strong>stays on the public
                        list</strong> as unmatched, so others can still choose to
                        fight them.
                    </p>
                    <p className="text-sm opacity-80">
                        Current active credits:{" "}
                        <strong>
                            {tokenCountsByFighterId[
                                fighterToRefund?.fighterId
                            ] ?? 0}
                        </strong>
                    </p>
                    <p className="text-sm opacity-80 mt-2">
                        Use this for fighters who could not be matched (odd
                        numbers, age/gender mismatch) — not for self-cancellations
                        (use Pull Out for those). The fighter will receive an
                        email.{" "}
                        <span className="font-bold text-error">
                            This action is recorded.
                        </span>
                    </p>
                    <div className="modal-action">
                        <form method="dialog">
                            <button
                                className="btn btn-ghost mr-2"
                                onClick={() => setFighterToRefund(null)}
                                disabled={refunding}
                            >
                                Cancel
                            </button>
                        </form>
                        <button
                            className="btn btn-primary"
                            onClick={handleConfirmRefund}
                            disabled={refunding || !fighterToRefund}
                        >
                            {refunding ? "Refunding..." : "Refund (issue 1 credit)"}
                        </button>
                    </div>
                </div>
                <form method="dialog" className="modal-backdrop">
                    <button onClick={() => setFighterToRefund(null)}>
                        close
                    </button>
                </form>
            </dialog>

            <dialog id="rejoin-dialog" className="modal">
                <div className="modal-box border">
                    <h3 className="font-bold text-lg">Rejoin with Credit</h3>
                    <p className="py-4">
                        Re-activate{" "}
                        <strong>
                            {fighterToRejoin?.fighter?.name || "this fighter"}
                        </strong>{" "}
                        by spending <strong>1 of their credits</strong>? This
                        clears the refund so they can be matched again.
                    </p>
                    <p className="text-sm opacity-80">
                        Active credits available:{" "}
                        <strong>
                            {tokenCountsByFighterId[
                                fighterToRejoin?.fighterId
                            ] ?? 0}
                        </strong>
                    </p>
                    {(tokenCountsByFighterId[fighterToRejoin?.fighterId] ?? 0) <
                        1 && (
                        <p className="text-sm font-bold text-error mt-2">
                            This fighter has no active credit to spend — the
                            rejoin will fail.
                        </p>
                    )}
                    <div className="modal-action">
                        <form method="dialog">
                            <button
                                className="btn btn-ghost mr-2"
                                onClick={() => setFighterToRejoin(null)}
                                disabled={rejoining}
                            >
                                Cancel
                            </button>
                        </form>
                        <button
                            className="btn btn-primary"
                            onClick={handleConfirmRejoin}
                            disabled={rejoining || !fighterToRejoin}
                        >
                            {rejoining ? "Rejoining..." : "Rejoin (spend 1 credit)"}
                        </button>
                    </div>
                </div>
                <form method="dialog" className="modal-backdrop">
                    <button onClick={() => setFighterToRejoin(null)}>
                        close
                    </button>
                </form>
            </dialog>
        </>
    );
}
